/**
 * =============================================================================
 * THEME EXTENSION GUIDE - WO Prestige Theme Customizations
 * =============================================================================
 *
 * This file demonstrates the correct approaches for extending the Prestige theme
 * while maintaining upgrade compatibility and following best practices.
 *
 * 📖 RECOMMENDED APPROACHES:
 *
 * 1. PROGRESSIVE ENHANCEMENT (✅ RECOMMENDED)
 *    - Find existing DOM elements and add new functionality
 *    - Use direct event listeners and DOM manipulation
 *    - Most reliable for adding features to existing components
 *
 *    Example:
 *    ```javascript
 *    const galleries = document.querySelectorAll('product-gallery');
 *    galleries.forEach(gallery => {
 *      gallery.addEventListener('wheel', handleWheel);
 *    });
 *    ```
 *
 * 2. CLASS EXTENSION WITH NEW CUSTOM ELEMENTS (✅ WORKS)
 *    - Import theme components and extend them
 *    - Create NEW custom elements (don't redefine existing ones)
 *    - Use different element names
 *
 *    Example:
 *    ```javascript
 *    import { ProductGallery } from "theme";
 *    class EnhancedProductGallery extends ProductGallery {
 *      // Your enhancements
 *    }
 *    window.customElements.define('enhanced-product-gallery', EnhancedProductGallery);
 *    ```
 *
 * ❌ APPROACHES THAT DON'T WORK:
 *
 * 1. REDEFINING EXISTING CUSTOM ELEMENTS (❌ FAILS)
 *    ```javascript
 *    // This will fail silently - you can't redefine existing custom elements
 *    window.customElements.define('product-gallery', MyCustomClass);
 *    ```
 *    Issue: Browser throws DOMException for duplicate custom element names
 *
 * 2. DIRECT THEME FILE MODIFICATION (❌ BAD PRACTICE)
 *    - Editing theme.js, theme.css directly
 *    - Prevents automatic theme updates
 *    - Makes debugging difficult
 *    - Hard to track custom vs. theme code
 *
 * 🔧 DEBUGGING CUSTOM ELEMENTS:
 *
 * - Check if element is already defined: `window.customElements.get('element-name')`
 * - Monitor element registration: `window.customElements.whenDefined('element-name')`
 * - Use console.log to verify DOM structure and element availability
 *
 * 🎯 BEST PRACTICES:
 *
 * 1. Always wait for DOM ready before enhancing elements
 * 2. Check if elements exist before adding functionality
 * 3. Use data attributes to mark enhanced elements (avoid double-enhancement)
 * 4. Gracefully handle missing elements or methods
 * 5. Add console logging for debugging (remove in production)
 * 6. Follow progressive enhancement principles
 *
 * 🚀 PERFORMANCE TIPS:
 *
 * - Use event delegation when possible
 * - Throttle frequent events (scroll, wheel, resize)
 * - Clean up event listeners when components are removed
 * - Avoid memory leaks with proper cleanup
 *
 * 📁 FILE STRUCTURE:
 *
 * - theme_custom.js  - Custom JavaScript (this file)
 * - theme_custom.scss - Custom styles
 * - theme.js - Original theme code (DO NOT MODIFY)
 * - theme.css - Original theme styles (DO NOT MODIFY)
 *
 * =============================================================================
 */

// -----------------------------------------------------------------------------
// Block theme.js ShareButton: WO uses its own share button implementation.
// Pre-register a no-op so theme.js skips its customElements.define check.
// -----------------------------------------------------------------------------
if (!window.customElements.get("share-button")) {
  window.customElements.define("share-button", class extends HTMLElement {});
}

// -----------------------------------------------------------------------------
// Product forms: ensure product forms have `name` attribute matching `id` so theme.js
// components (ProductGallery, VariantPicker, ProductStickyBar, BuyButtons, etc.)
// can query them via document.forms[formId]. (Shopify form tag only sets id).
// -----------------------------------------------------------------------------
function ensureProductFormNames() {
  document.querySelectorAll("form[id]").forEach((f) => {
    if (f.id && !f.name) f.name = f.id;
  });
  document.querySelectorAll("product-gallery, product-sticky-bar, variant-picker, product-rerender, buy-buttons").forEach((el) => {
    const fid = el.getAttribute("form") || el.getAttribute("form-id") || el.getAttribute("observe-form");
    if (fid) {
      const f = document.getElementById(fid);
      if (f && !f.name) f.name = fid;
    }
  });
}

function syncProductGalleryVariant(gallery, variant) {
  if (!gallery || !variant) return;

  const carousel = gallery.querySelector("scroll-carousel, .product-gallery__carousel");
  if (!carousel) return;

  let targetMediaId = null;
  if (variant.featured_media && variant.featured_media.id) {
    targetMediaId = String(variant.featured_media.id);
  } else if (variant.featured_image && variant.featured_image.id) {
    targetMediaId = String(variant.featured_image.id);
  }

  if (targetMediaId) {
    const allMedia = Array.from(carousel.querySelectorAll(".product-gallery__media"));
    const visibleMedia = allMedia.filter((m) => !m.hasAttribute("hidden"));
    const targetMedia = allMedia.find((m) => m.getAttribute("data-media-id") === targetMediaId);

    if (targetMedia) {
      const targetIndex = visibleMedia.indexOf(targetMedia);
      if (targetIndex !== -1) {
        if (typeof carousel.select === "function") {
          carousel.select(targetIndex, { instant: true });
        } else {
          targetMedia.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
        }
      }
    }
  }
}

ensureProductFormNames();
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", ensureProductFormNames);
}

document.addEventListener("variant:change", (event) => {
  const { formId, variant } = event.detail || {};
  if (!variant) return;

  let galleries = [];
  if (formId) {
    galleries = Array.from(document.querySelectorAll(`product-gallery[form="${formId}"]`));
  }
  if (galleries.length === 0 && event.target) {
    const container = event.target.closest("x-modal, product-rerender, .quick-buy-modal__content, .product, product-gallery");
    if (container) {
      if (container.tagName === "PRODUCT-GALLERY") {
        galleries = [container];
      } else {
        galleries = Array.from(container.querySelectorAll("product-gallery"));
      }
    }
  }

  galleries.forEach((gallery) => {
    syncProductGalleryVariant(gallery, variant);
  });
});

if (typeof MutationObserver !== "undefined" && document.body) {
  const mo = new MutationObserver((mutations) => {
    const hasFormOrComponent = mutations.some((m) =>
      Array.from(m.addedNodes).some(
        (n) =>
          n.nodeType === 1 &&
          (n.tagName === "FORM" ||
            n.tagName === "PRODUCT-GALLERY" ||
            n.tagName === "PRODUCT-STICKY-BAR" ||
            n.tagName === "VARIANT-PICKER" ||
            n.querySelector?.("form, product-gallery, product-sticky-bar, variant-picker")),
      ),
    );
    if (hasFormOrComponent) ensureProductFormNames();
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

// -----------------------------------------------------------------------------
// Product sticky bar fallback when form is hidden (configurator flow): the theme
// observes the form via IntersectionObserver, but a hidden form has no layout
// box so the observer never fires. We observe the visible .product container
// instead and toggle is-visible on the sticky bar.
// -----------------------------------------------------------------------------
function initStickyBarFallbackForHiddenForm() {
  document.querySelectorAll("product-sticky-bar").forEach((bar) => {
    if (bar.dataset.stickyBarFallbackInit) return;
    const fid = bar.getAttribute("form");
    if (!fid) return;
    const form = document.getElementById(fid);
    if (!form) return;
    const isFormHidden =
      form.hasAttribute("hidden") ||
      (typeof form.getBoundingClientRect === "function" &&
        form.getBoundingClientRect().height === 0);
    if (!isFormHidden) return;
    const productBlock = form.closest(".product");
    const footer = document.querySelector(".shopify-section--footer");
    if (!productBlock || !footer) return;
    bar.dataset.stickyBarFallbackInit = "1";
    let latestProductPast = false;
    let latestFooterPast = false;
    const updateVisible = () => {
      bar.classList.toggle("is-visible", latestFooterPast && latestProductPast);
    };
    const recheckVisible = () => {
      const productRect = productBlock.getBoundingClientRect();
      const footerRect = footer.getBoundingClientRect();
      latestProductPast = productRect.bottom < 0;
      latestFooterPast = footerRect.top >= window.innerHeight;
      updateVisible();
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === productBlock) {
            latestProductPast =
              !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          }
          if (entry.target === footer) {
            latestFooterPast = !entry.isIntersecting;
          }
        });
        updateVisible();
      },
      { root: null, rootMargin: "0px", threshold: 0 },
    );
    io.observe(productBlock);
    io.observe(footer);

    // Re-check after load so sticky bar shows when user refreshed while scrolled past product (scroll restore happens around then)
    window.addEventListener("load", recheckVisible, { once: true });
  });
}
if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    initStickyBarFallbackForHiddenForm,
  );
} else {
  initStickyBarFallbackForHiddenForm();
}

// -----------------------------------------------------------------------------
// Cart drawer cross-sell: make horizontal-product-card clickable like a coupon
// -----------------------------------------------------------------------------
function initCartDrawerCrossSellClick() {
  const root = document;
  if (!root) return;

  root.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest(
      ".cart-drawer__complementary-products .horizontal-product-card",
    );
    if (!card) return;

    // 只在折價券 cross-sell 卡片裡接管點擊：
    // - 任何 a（image / title）都不導向產品，而是打開 quick-buy
    // - 其他原生控制元件照常運作
    const link = target.closest("a");
    const control = target.closest("button, input, select, textarea");
    if (!link && control) return;

    // Prefer quick-buy modal trigger, fall back to submit button (single-variant)
    const trigger =
      card.querySelector("button[aria-controls]") ||
      card.querySelector('button[type="submit"]');
    if (!trigger) return;

    event.preventDefault();
    trigger.click();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCartDrawerCrossSellClick);
} else {
  initCartDrawerCrossSellClick();
}

// Update Product Details
export function updateProductDetails(event) {
  // Custom Variant Change Description James Yang
  // Replace the inner or outer html with custom json data parsed from liquid render/output

  const product = JSON.parse(
    document.querySelector("#product_metafield_data").innerText,
  );
  const product_description = product["description"];

  const allVariantMetafieldDescription = JSON.parse(
    document.querySelector("#variant_metafield_data").textContent,
  );
  const descriptionElement =
    document.querySelector("#description") ||
    document.querySelector(".product-content-below-gallery__description");
  if (descriptionElement != undefined) {
    const variantMetafieldDef =
      allVariantMetafieldDescription[event.detail.variant["id"]];
    const modalContent = descriptionElement.querySelector(
      ".card-modal__content",
    );

    if (modalContent) {
      // 如果存在 card-modal，只更新 modal 內容區域，保持 modal 結構完整
      if (variantMetafieldDef != "") {
        modalContent.innerHTML = variantMetafieldDef + product_description;
      } else {
        modalContent.innerHTML = product_description;
      }
    } else {
      // 如果沒有 card-modal，直接更新整個元素（例如 main-product.liquid 的情況）
      if (variantMetafieldDef != "") {
        descriptionElement.innerHTML =
          variantMetafieldDef + product_description;
      } else {
        descriptionElement.innerHTML = product_description;
      }
    }
  }

  // Specifications
  const allVariantMetafieldSpecification = JSON.parse(
    document
      .querySelector("#variant_metafield_specification")
      .textContent.replace(/\n/g, ""),
  ); // Delete empty next line
  var variantMetafieldSpecs =
    allVariantMetafieldSpecification[event.detail.variant["id"]];
  const specificationElement = document.querySelector(".specification");
  if (variantMetafieldSpecs != undefined && specificationElement != undefined) {
    specificationElement.outerHTML = variantMetafieldSpecs;
  }

  // Addon-Products、
  // NOTE: This uses server-pre-rendered HTML from Liquid (trusted content from Shopify).
  // The JSON is generated in product-info.liquid via render 'addon-products' per variant.
  const addonDataEl = document.querySelector(
    "#variant_metafield_addon-products",
  );
  if (addonDataEl) {
    const allVariantMetafieldAddonProducts = JSON.parse(
      addonDataEl.textContent.replace(/\n/g, ""),
    );
    const variantMetafieldAddons =
      allVariantMetafieldAddonProducts[event.detail.variant["id"]];
    const addonProductsElement = document.querySelector(".addon-products");
    if (
      variantMetafieldAddons != undefined &&
      addonProductsElement != undefined
    ) {
      addonProductsElement.innerHTML = variantMetafieldAddons;
    }
  }
}

// Main product: Prestige dispatches bubbling `variant:change` on the product form
// (see theme.js). Wire metafield-driven description / specs / add-ons updates.
(function initUpdateProductDetailsOnVariantChange() {
  document.addEventListener("variant:change", (e) => {
    if (!e.detail?.variant) return;
    if (
      !document.getElementById("product_metafield_data") ||
      !document.getElementById("variant_metafield_data")
    ) {
      return;
    }
    try {
      updateProductDetails(e);
    } catch (err) {
      // A failed detail refresh must not break the variant change.
    }
  });
})();

/**
 * =============================================================================
 * PRODUCT GALLERY MOUSE WHEEL ENHANCEMENT
 * =============================================================================
 *
 * This enhancement adds mouse wheel scroll functionality to product galleries.
 *
 * APPROACH: Progressive Enhancement (✅ Recommended)
 * - Finds existing product-gallery elements
 * - Adds wheel event listeners to existing DOM nodes
 * - Uses the carousel's built-in navigation methods
 * - Doesn't interfere with existing functionality
 *
 * FEATURES:
 * - Mouse wheel up/down changes product images
 * - Throttled to prevent rapid scrolling (150ms)
 * - Only works when gallery has multiple images
 * - Prevents page scroll when over gallery
 * - Works with all existing gallery features (thumbnails, dots, etc.)
 *
 * =============================================================================
 */

if (window.themeVariables?.settings) {
  window.themeVariables.settings.staggerMenuApparition = false;
}

// Enhanced Product Gallery with Mouse Wheel Scroll
function enhanceProductGalleryWithWheelScroll() {
  // Wait for DOM to be fully loaded
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      enhanceProductGalleryWithWheelScroll,
    );
    return;
  }

  // Find all product gallery elements
  const galleries = document.querySelectorAll("product-gallery");

  galleries.forEach((gallery, index) => {
    // Skip if already enhanced
    if (gallery.hasAttribute("data-wheel-enhanced")) {
      return;
    }

    // Mark as enhanced
    gallery.setAttribute("data-wheel-enhanced", "true");

    // Try to find carousel elements
    const scrollCarousel = gallery.querySelector("scroll-carousel");
    const carouselDiv = gallery.querySelector(".product-gallery__carousel");

    let wheelThrottled = false;

    // Add mouse wheel event listener
    const handleWheel = (event) => {
      // Prevent default scroll behavior when over the gallery
      event.preventDefault();

      // Try to find the carousel element
      let carousel = scrollCarousel || carouselDiv;
      if (!carousel) return;

      // Check if carousel has multiple cells
      const cells = carousel.querySelectorAll(
        ".product-gallery__media:not([hidden])",
      );
      if (cells.length <= 1) return;

      // Throttle wheel events to prevent rapid scrolling
      if (wheelThrottled) return;

      wheelThrottled = true;
      setTimeout(() => {
        wheelThrottled = false;
      }, 150);

      // Navigate based on scroll direction
      if (event.deltaY > 0) {
        // Scroll down - next image
        if (typeof carousel.next === "function") {
          carousel.next();
        } else if (typeof carousel.select === "function") {
          const currentIndex = carousel.selectedIndex || 0;
          const nextIndex = (currentIndex + 1) % cells.length;
          carousel.select(nextIndex);
        }
      } else {
        // Scroll up - previous image
        if (typeof carousel.previous === "function") {
          carousel.previous();
        } else if (typeof carousel.select === "function") {
          const currentIndex = carousel.selectedIndex || 0;
          const prevIndex =
            currentIndex > 0 ? currentIndex - 1 : cells.length - 1;
          carousel.select(prevIndex);
        }
      }
    };

    // Add the wheel event listener
    gallery.addEventListener("wheel", handleWheel, { passive: false });
  });
}

// Initialize when DOM is ready
enhanceProductGalleryWithWheelScroll();

// Also initialize when new content is loaded (for dynamic content)
document.addEventListener(
  "shopify:section:load",
  enhanceProductGalleryWithWheelScroll,
);

/**
 * =============================================================================
 * HEADER SIDEBAR ACCORDION DISCLOSURE ENHANCEMENT
 * =============================================================================
 *
 * Extends the original theme's accordion disclosure functionality to work
 * with data-follow-link attributes in the header sidebar.
 *
 * This enhancement works with the theme's existing CustomDetails class
 * and doesn't modify the original behavior - it just ensures the
 * data-follow-link functionality works properly.
 *
 * =============================================================================
 */

function enhanceHeaderSidebarAccordionDisclosure() {
  // Wait for DOM to be fully loaded
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      enhanceHeaderSidebarAccordionDisclosure,
    );
    return;
  }

  // Find the header sidebar element
  const headerSidebar = document.getElementById("sidebar-menu");
  if (!headerSidebar) return;

  // Find all accordion disclosure elements within the header sidebar
  const accordionDisclosures = headerSidebar.querySelectorAll(
    "accordion-disclosure",
  );

  accordionDisclosures.forEach((accordion, index) => {
    // Skip if already enhanced
    if (accordion.hasAttribute("data-link-enhanced")) {
      return;
    }

    // Mark as enhanced
    accordion.setAttribute("data-link-enhanced", "true");

    // Find the summary element within this accordion
    const summary = accordion.querySelector("summary[data-follow-link]");
    if (!summary) {
      return;
    }

    // Override the default behavior to make entire summary clickable for navigation
    const linkUrl = summary.getAttribute("data-follow-link");
    const arrow = summary.querySelector(".animated-plus");
    const details = accordion.querySelector("details");

    if (!linkUrl || !arrow || !details) return;

    // Make entire summary clickable for navigation
    summary.style.cursor = "pointer";

    // Handle summary clicks for navigation
    const handleSummaryClick = (event) => {
      // Check if the click was on the animated plus (arrow)
      if (event.target === arrow || arrow.contains(event.target)) {
        // Let the arrow handle accordion toggle
        return;
      }

      // For all other clicks, prevent accordion expansion and navigate to the link
      event.preventDefault();
      event.stopPropagation();
      window.location.href = linkUrl;
    };

    // Handle arrow clicks for accordion toggle
    const handleArrowClick = (event) => {
      event.preventDefault();
      event.stopPropagation();

      // Use the theme's accordion toggle method to maintain animations
      accordion.toggle();
    };

    // Increase arrow clickable area
    arrow.style.padding = "20px";
    arrow.style.margin = "-8px";
    arrow.style.cursor = "pointer";
    arrow.style.display = "inline-block";

    // Add event listeners with capture phase to run before theme handlers
    summary.addEventListener("click", handleSummaryClick, true);
    arrow.addEventListener("click", handleArrowClick, true);
  });
}

// Initialize when DOM is ready
enhanceHeaderSidebarAccordionDisclosure();

// Also initialize when new content is loaded (for dynamic content)
document.addEventListener(
  "shopify:section:load",
  enhanceHeaderSidebarAccordionDisclosure,
);

/**
 * =============================================================================
 * IMAGES WITH TEXT SCROLL - MOBILE ENHANCEMENT
 * =============================================================================
 * Mobile behavior:
 * - Image stays sticky at the top (handled by CSS)
 * - Only text scrolls vertically
 * - Cross-fade images when active item changes
 * - Top/bottom text fade handled via CSS mask
 * =============================================================================
 */

function enhanceImagesWithTextScrollMobile() {
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      enhanceImagesWithTextScrollMobile,
    );
    return;
  }

  // Prevent double-initialization
  if (window.__iwtsMobileEnhanced) return;
  window.__iwtsMobileEnhanced = true;

  const roots = Array.from(
    document.querySelectorAll("images-with-text-scroll"),
  );
  if (roots.length === 0) return;

  const isMobile = () => window.matchMedia("(max-width: 1000px)").matches;

  const instances = roots
    .map((root) => {
      const container = root.querySelector(
        ".images-with-text-scroll__container",
      );
      const items = Array.from(
        root.querySelectorAll(".images-with-text-scroll__item"),
      );
      if (!items.length) return null;

      // Build mapping: for each item index, map to image index (-1 if no image)
      const sourceImages = [];
      const itemToStickyIndex = [];
      let lastImageIdx = -1;
      items.forEach((item) => {
        const img = item.querySelector(".images-with-text-scroll__image");
        if (img) {
          lastImageIdx += 1;
          sourceImages.push(img);
          itemToStickyIndex.push(lastImageIdx);
        } else {
          itemToStickyIndex.push(-1); // No image for this item
        }
      });

      if (!sourceImages.length) return null;

      // Inject shared sticky overlay if not exists
      let sticky = root.querySelector(".images-with-text-scroll__sticky");
      if (!sticky) {
        sticky = document.createElement("div");
        sticky.className = "images-with-text-scroll__sticky";
        const inner = document.createElement("div");
        inner.className = "images-with-text-scroll__sticky-inner";
        sticky.appendChild(inner);
        container.insertBefore(sticky, container.firstChild);

        // Clone all images into sticky in their source order
        sourceImages.forEach((img, idx) => {
          const clone = img.cloneNode(true);
          clone.classList.add("images-with-text-scroll__sticky-image");
          clone.removeAttribute("sizes");
          clone.removeAttribute("srcset");
          if (idx === 0) clone.classList.add("is-active");
          inner.appendChild(clone);
        });
      }

      const stickyImages = Array.from(
        sticky.querySelectorAll(".images-with-text-scroll__sticky-image"),
      );

      let currentIndex = Math.max(
        0,
        items.findIndex((el) => el.classList.contains("is-selected")),
      );
      if (currentIndex === -1) currentIndex = 0;

      // Ensure initial active based on mapping
      const initialStickyIdx = Math.min(
        stickyImages.length - 1,
        Math.max(0, itemToStickyIndex[currentIndex]),
      );
      stickyImages.forEach((img, i) =>
        img.classList.toggle("is-active", i === initialStickyIdx),
      );

      return {
        root,
        items,
        sticky,
        stickyImages,
        itemToStickyIndex,
        currentIndex,
        lastScore: 0,
      };
    })
    .filter(Boolean);

  const getVisibilityScore = (
    el,
    contentTop,
    contentBottom,
    preferredCenterY,
  ) => {
    const rect = el.getBoundingClientRect();
    const contentHeight = Math.max(1, contentBottom - contentTop);
    const visible = Math.max(
      0,
      Math.min(rect.bottom, contentBottom) - Math.max(rect.top, contentTop),
    );
    const center = rect.top + rect.height / 2;
    const centerDist = Math.abs(center - preferredCenterY);
    const centerScore = Math.max(0, 1 - centerDist / contentHeight);
    const areaScore =
      visible / Math.max(1, Math.min(rect.height, contentHeight));
    return areaScore * 0.6 + centerScore * 0.4;
  };

  let ticking = false;
  const update = () => {
    ticking = false;
    if (!isMobile()) return;
    instances.forEach((inst) => {
      if (!inst.items.length) return;
      const stickyRect = inst.sticky.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // Assume 1:1 image height on mobile regardless of actual ratio
      const assumedStickyHeight = Math.min(vw, vh);
      const contentTop = stickyRect.top + assumedStickyHeight;
      const contentBottom = vh;
      const belowOffset = Math.max(
        64,
        Math.min(160, assumedStickyHeight * 0.35),
      );
      const preferredCenterY = contentTop + belowOffset; // activate well below image bottom

      // Calculate scores for all items
      const scores = inst.items.map((item, i) => ({
        index: i,
        score: getVisibilityScore(
          item,
          contentTop,
          contentBottom,
          preferredCenterY,
        ),
      }));

      // Find best candidate
      const bestCandidate = scores.reduce((best, current) =>
        current.score > best.score ? current : best,
      );

      // Enhanced hysteresis: more sensitive deactivation, less sensitive activation
      const currentScore =
        scores.find((s) => s.index === inst.currentIndex)?.score || 0;
      // Adjusted thresholds for taller items (150vh)
      const activationThreshold = 0.5; // Lowered from 0.8 to ensure activation with tall items
      const deactivationThreshold = 0.3; // Lowered from 0.6 to keep active longer

      let shouldSwitch = false;
      if (bestCandidate.index !== inst.currentIndex) {
        if (
          currentScore < deactivationThreshold &&
          bestCandidate.score > activationThreshold
        ) {
          shouldSwitch = true; // Current is weak, new is strong
        } else if (bestCandidate.score > currentScore + 0.3) {
          shouldSwitch = true; // New is significantly better
        }
      }

      if (shouldSwitch) {
        inst.items.forEach((el, idx) =>
          el.classList.toggle("is-selected", idx === bestCandidate.index),
        );

        const mappedIdx = inst.itemToStickyIndex[bestCandidate.index];
        // If mappedIdx is -1 or undefined, we don't show any image
        if (mappedIdx === undefined || mappedIdx === -1) {
          inst.stickyImages.forEach((img) => img.classList.remove("is-active"));
        } else {
          const safeIdx = Math.min(
            inst.stickyImages.length - 1,
            Math.max(0, mappedIdx),
          );
          inst.stickyImages.forEach((img, i) =>
            img.classList.toggle("is-active", i === safeIdx),
          );
        }

        inst.currentIndex = bestCandidate.index;
        inst.lastScore = bestCandidate.score;
      } else {
        // Special case: if current is last item and score is low, hide the image
        const isLastItem = inst.currentIndex === inst.items.length - 1;
        const currentScore =
          scores.find((s) => s.index === inst.currentIndex)?.score || 0;
        if (isLastItem && currentScore < 0.3) {
          // Hide all images when last item is no longer prominent
          inst.stickyImages.forEach((img, i) =>
            img.classList.toggle("is-active", false),
          );
        }
      }

      // [PARALLAX EFFECT] Apply slow drift to the active text
      // We apply it to all items to ensure smooth transitions if multiple are visible
      inst.items.forEach((item, i) => {
        const textWrapper = item.querySelector(
          ".images-with-text-scroll__text",
        );
        if (!textWrapper) return;

        // Calculate offset based on item position relative to viewport
        // Use a gentle factor (e.g., 0.15) to create a subtle "floating" drift
        // As item scrolls UP (rect.top decreases), text moves UP slightly (negative translateY)
        const rect = item.getBoundingClientRect();
        // Determine base offset based on whether it has an image (matches CSS top)
        // This ensures the parallax is relative to its sticky lock position
        const driftFactor = 0.15; // 15% of scroll speed
        // REMOVE Math.round to prevent sub-pixel jitter
        // Use translate3d to force hardware acceleration
        const offset = rect.top * driftFactor;

        textWrapper.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
      });
    });
  };

  const requestTick = () => {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(update);
    }
  };

  // Initial and listeners
  requestTick();
  window.addEventListener("scroll", requestTick, { passive: true });
  window.addEventListener("resize", requestTick);
}

enhanceImagesWithTextScrollMobile();

// Disable horizontal swipe for images-with-text-scroll while keeping vertical scroll
function disableImagesWithTextScrollHorizontalSwipe() {
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      disableImagesWithTextScrollHorizontalSwipe,
    );
    return;
  }

  const scrollElements = document.querySelectorAll("images-with-text-scroll");
  scrollElements.forEach((element) => {
    // Override the allowSwipe getter to always return false
    Object.defineProperty(element, "allowSwipe", {
      get: function () {
        return false;
      },
      configurable: true,
    });
  });
}

disableImagesWithTextScrollHorizontalSwipe();
document.addEventListener(
  "shopify:section:load",
  enhanceImagesWithTextScrollMobile,
);
document.addEventListener(
  "shopify:section:load",
  disableImagesWithTextScrollHorizontalSwipe,
);

/**
 * =============================================================================
 * MEGA MENU HOVER INTENT FIX
 * =============================================================================
 */

function enhanceMegaMenuHoverIntent() {
  const MEGA_MENU_SELECTOR = "mega-menu-disclosure";
  const OPEN_DELAY_MS = 50;
  const CLOSE_DELAY_MS = 50;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", enhanceMegaMenuHoverIntent);
    return;
  }

  const megaMenus = document.querySelectorAll(MEGA_MENU_SELECTOR);
  const isDesktop = window.matchMedia("(pointer: fine)").matches;

  if (!isDesktop) return;

  // Global state to manage instant switching
  let globalCloseTimer = null;
  let globalOpenTimer = null;
  let activeMenu = null;

  megaMenus.forEach((menu) => {
    if (menu.hasAttribute("data-hover-intent-enhanced")) return;
    menu.setAttribute("data-hover-intent-enhanced", "true");

    if (menu.getAttribute("trigger") === "hover") {
      menu.setAttribute("trigger", "manual-hover");
    }

    const details = menu.querySelector("details");
    if (!details) return;

    details.addEventListener("mouseenter", () => {
      clearTimeout(globalCloseTimer);
      clearTimeout(globalOpenTimer);

      // [Instant Switch Logic]
      // If another menu is active, switch immediately
      if (activeMenu && activeMenu !== menu) {
        activeMenu.toggle(false);
        menu.toggle(true);
        activeMenu = menu;
        return;
      }

      if (details.open) {
        activeMenu = menu;
        return;
      }

      // [Hover Intent Logic]
      globalOpenTimer = setTimeout(() => {
        menu.toggle(true);
        activeMenu = menu;
      }, OPEN_DELAY_MS);
    });

    details.addEventListener("mouseleave", () => {
      clearTimeout(globalOpenTimer);

      globalCloseTimer = setTimeout(() => {
        menu.toggle(false);
        if (activeMenu === menu) activeMenu = null;
      }, CLOSE_DELAY_MS);
    });

    details.addEventListener("focusin", () => {
      clearTimeout(globalCloseTimer);
      if (activeMenu && activeMenu !== menu) {
        activeMenu.toggle(false);
      }
      menu.toggle(true);
      activeMenu = menu;
    });

    details.addEventListener("focusout", (e) => {
      if (!details.contains(e.relatedTarget)) {
        menu.toggle(false);
        if (activeMenu === menu) activeMenu = null;
      }
    });
  });
}

/**
 * Mega Menu Animation Fix
 *
 * Fixes: theme.js contentElement getter sometimes returns <script>/<slot> instead of .mega-menu.
 */
(function patchMegaMenuAnimations() {
  const ANIMATION = {
    open: {
      keyframes: [{ opacity: 0 }, { opacity: 1 }],
      duration: 250,
      easing: "ease",
    },
    close: {
      keyframes: [{ opacity: 1 }, { opacity: 0 }],
      duration: 400,
      easing: "ease",
    },
  };

  injectChildrenStyles();
  patchMegaMenuDisclosure();

  function injectChildrenStyles() {
    const selectors = [
      "mega-menu-disclosure details[open] .mega-menu__promo",
      "mega-menu-disclosure details[open] .mega-menu__thumbnail",
      "mega-menu-disclosure details[open] .mega-menu__linklist",
      "mega-menu-disclosure details[open] .mega-menu__linklist > li",
    ];
    const style = document.createElement("style");
    style.textContent = `
      ${selectors.join(",")} {
        opacity: 1 !important;
        transform: none !important;
        visibility: visible !important;
        transition: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  function resolveMenuElement(element) {
    if (!element) return null;
    if (element.classList?.contains("mega-menu")) return element;

    const details = element.closest("details");
    return details?.querySelector(".mega-menu") || null;
  }

  function animateMenu(target, direction) {
    const config = ANIMATION[direction];

    if (direction === "open") {
      target.style.visibility = "visible";
      target.style.pointerEvents = "auto";
    } else {
      target.style.pointerEvents = "none";
    }

    target.getAnimations().forEach((a) => a.cancel());
    target.style.transition = "none";
    target.style.willChange = "opacity";

    const animation = target.animate(config.keyframes, {
      duration: config.duration,
      easing: config.easing,
      fill: "forwards",
    });

    animation.finished
      .then(() => {
        target.style.willChange = "";
        target.style.transition = "";

        if (direction === "close") {
          target.style.visibility = "hidden";
        }
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        throw e;
      });

    const safeFinished = animation.finished.catch((e) => {
      if (e.name === "AbortError") return;
      throw e;
    });

    return { finished: safeFinished };
  }

  function patchMegaMenuDisclosure() {
    const createHandler = (direction) =>
      function () {
        const target = resolveMenuElement(this.contentElement);
        if (!target) return { finished: Promise.resolve() };
        return animateMenu(target, direction);
      };

    customElements.whenDefined("mega-menu-disclosure").then(() => {
      const MegaMenuClass = customElements.get("mega-menu-disclosure");
      if (!MegaMenuClass) return;

      MegaMenuClass.prototype.createShowAnimationControls =
        createHandler("open");
      MegaMenuClass.prototype.createHideAnimationControls =
        createHandler("close");
    });

    const patchInstances = () => {
      document.querySelectorAll("mega-menu-disclosure").forEach((el) => {
        if (el._animationPatched) return;
        el.createShowAnimationControls = createHandler("open");
        el.createHideAnimationControls = createHandler("close");
        el._animationPatched = true;
      });
    };

    patchInstances();
    document.addEventListener("DOMContentLoaded", patchInstances);
    new MutationObserver(patchInstances).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
})();

enhanceMegaMenuHoverIntent();

/**
 * =============================================================================
 * GALLERY TAB - X-TABS INHERITANCE WITHOUT ANIMATIONS
 * =============================================================================
 *
 * 繼承 Prestige 主題的 x-tabs 功能，但完全移除動畫效果
 * 簡化但完整的實現，確保與現有 JavaScript 兼容
 */

class GalleryTab extends HTMLElement {
  constructor() {
    super();
    this._buttons = [];
    this._panels = [];
    this._selectedIndex = 0;
    this._isInitialized = false;
    this._fadeTimeout = null;
  }

  connectedCallback() {
    // 避免重複初始化
    if (this._isInitialized) return;

    // 延遲初始化以確保 DOM 準備就緒
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this._initialize());
    } else {
      // 使用 requestAnimationFrame 確保 DOM 完全準備
      requestAnimationFrame(() => this._initialize());
    }
  }

  _initialize() {
    try {
      // 設置 Shadow DOM（如果有 template）
      this._setupShadowDOM();

      // 初始化按鈕和面板
      this._updateButtonsAndPanels();

      // 設置事件監聽器
      this._setupEventListeners();

      // 設置初始狀態
      this._setupInitialState();

      // 監聽視窗大小變化以更新 position 狀態
      this._setupResponsiveListener();

      this._isInitialized = true;
    } catch (error) {
      // 降級到基本實現
      this._fallbackInitialization();
    }
  }

  _setupShadowDOM() {
    // 設置 Shadow DOM，與 x-tabs 保持一致
    if (!this.shadowRoot) {
      const template = this.querySelector("template");
      if (template) {
        this.attachShadow({ mode: "open" }).appendChild(
          template.content.cloneNode(true),
        );
      }
    }
  }

  _updateButtonsAndPanels() {
    if (this.shadowRoot) {
      // 使用 Shadow DOM 的 slot
      const titleSlot = this.shadowRoot.querySelector('slot[name="title"]');
      const contentSlot =
        this.shadowRoot.querySelector('slot[name="content"]') ||
        this.shadowRoot.querySelector("slot:not([name])");

      this._buttons = Array.from(titleSlot?.assignedElements() || []);
      this._panels = Array.from(contentSlot?.assignedElements() || []);
    } else {
      // 直接查詢（如果沒有 Shadow DOM）
      this._buttons = Array.from(this.querySelectorAll('button[role="tab"]'));
      this._panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    }

    // 確保我們有正確的元素
    this._buttons = this._buttons.filter(
      (btn) => btn.matches && btn.matches('button, [role="tab"]'),
    );
    this._panels = this._panels.filter(
      (panel) =>
        panel.matches && panel.matches('[role="tabpanel"], .tab-panel'),
    );
  }

  _setupEventListeners() {
    // Tab 按鈕點擊事件
    this.addEventListener("click", (event) => {
      const button = event.target.closest('button, [role="tab"]');
      if (button && this._buttons.includes(button)) {
        const index = this._buttons.indexOf(button);
        this.selectedIndex = index;
      }
    });

    // 鍵盤導航
    this.addEventListener("keydown", (event) => {
      const focusedButton = event.target.closest('button, [role="tab"]');
      if (!focusedButton || !this._buttons.includes(focusedButton)) return;

      const currentIndex = this._selectedIndex;
      let newIndex = currentIndex;

      switch (event.key) {
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          newIndex =
            currentIndex > 0 ? currentIndex - 1 : this._buttons.length - 1;
          break;
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          newIndex =
            currentIndex < this._buttons.length - 1 ? currentIndex + 1 : 0;
          break;
        case "Home":
          event.preventDefault();
          newIndex = 0;
          break;
        case "End":
          event.preventDefault();
          newIndex = this._buttons.length - 1;
          break;
      }

      if (newIndex !== currentIndex) {
        this.selectedIndex = newIndex;
        this._buttons[newIndex].focus();
      }
    });

    // 監聽 slot 變化（動態內容）
    if (this.shadowRoot) {
      this.shadowRoot.addEventListener("slotchange", () => {
        setTimeout(() => this._updateButtonsAndPanels(), 0);
      });
    }
  }

  _setupInitialState() {
    // 從屬性讀取初始索引和動畫模式
    const initialIndex = parseInt(this.getAttribute("selected-index")) || 0;
    this._animationMode = this.getAttribute("data-animation-mode") || "none";
    this._selectedIndex = Math.min(initialIndex, this._buttons.length - 1);

    // 初始化當前 position 狀態
    this._updateCurrentPosition();

    // 確保初始狀態正確設置
    this._buttons.forEach((button, i) => {
      button.setAttribute(
        "aria-selected",
        i === this._selectedIndex ? "true" : "false",
      );
      button.setAttribute("tabindex", i === this._selectedIndex ? "0" : "-1");
    });

    this._panels.forEach((panel, i) => {
      panel.hidden = i !== this._selectedIndex;
      // 清除任何可能存在的 transform，確保初始狀態乾淨
      panel.style.transform = "";
      panel.style.opacity = "";
    });
  }

  _updateCurrentPosition() {
    // 根據當前視窗大小決定使用 mobile 還是 desktop position
    const isMobile = window.matchMedia("(max-width: 999px)").matches;
    const positionKey = isMobile
      ? "data-mobile-position"
      : "data-desktop-position";
    this._currentPosition = this.getAttribute(positionKey) || "top";
  }

  _setupResponsiveListener() {
    // 監聽視窗大小變化
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const oldPosition = this._currentPosition;
        this._updateCurrentPosition();

        // 如果 position 發生變化，清理所有動畫狀態
        if (oldPosition !== this._currentPosition) {
          this._resetAllAnimations();
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);
  }

  _resetAllAnimations() {
    // 清理所有面板上的動畫狀態
    this._panels.forEach((panel) => {
      // 清理所有動畫
      panel.getAnimations().forEach((anim) => anim.cancel());

      // 清理所有 inline styles
      [
        "position",
        "top",
        "left",
        "width",
        "height",
        "zIndex",
        "pointerEvents",
        "opacity",
        "transition",
        "transform",
        "display",
      ].forEach((p) => {
        panel.style[p] = "";
      });

      // 清理面板內部的元素動畫
      const images = panel.querySelectorAll("before-after, .prose:first-child");
      const texts = panel.querySelectorAll(".prose.responsive-tab-content");

      [...images, ...texts].forEach((el) => {
        el.getAnimations().forEach((anim) => anim.cancel());
        ["opacity", "transform", "transition"].forEach((p) => {
          el.style[p] = "";
        });
      });
    });

    // 清理容器動畫
    const container = this._panels[0]?.parentElement;
    if (container) {
      container.getAnimations().forEach((anim) => anim.cancel());
      ["height", "overflow", "transition", "position"].forEach((p) => {
        container.style[p] = "";
      });
    }

    // 清除 fade timeout
    if (this._fadeTimeout) {
      clearTimeout(this._fadeTimeout);
      this._fadeTimeout = null;
    }
  }

  // 獲取動畫模式
  get animationMode() {
    return (
      this._animationMode || this.getAttribute("data-animation-mode") || "none"
    );
  }

  _showTab(index, isInternal = false) {
    const animationMode = this.animationMode;
    const previousIndex = this._selectedIndex;

    // 如果是相同索引且不是內部調用，不需要做任何事
    if (!isInternal && index === previousIndex) return;

    // 更新按鈕狀態
    this._buttons.forEach((button, i) => {
      button.setAttribute("aria-selected", i === index ? "true" : "false");
      button.setAttribute("tabindex", i === index ? "0" : "-1");
    });

    // 根據動畫模式執行切換
    if (animationMode === "none") {
      // None 模式：立即切換，無動畫
      this._panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    } else if (animationMode === "fade") {
      // Fade 模式：淡入淡出動畫
      this._showTabWithFade(index, previousIndex);
    } else if (animationMode === "slide") {
      // Slide 模式：滑動 + 淡入淡出動畫（模擬 x-tabs 原始動畫）
      this._showTabWithSlide(index, previousIndex);
    }

    this._selectedIndex = index;
    this.setAttribute("selected-index", index.toString());

    // 觸發變更事件，保持與現有代碼兼容
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: { index, previousIndex },
        bubbles: true,
      }),
    );
  }

  // 輔助方法：重置 Fade 動畫狀態
  _resetFade(keepVisibleIndex) {
    // 1. 清除計時器
    if (this._fadeTimeout) {
      clearTimeout(this._fadeTimeout);
      this._fadeTimeout = null;
    }

    // 2. 清理容器樣式
    const container = this._panels[0]?.parentElement;
    if (container) {
      container.style.height = "";
      container.style.overflow = "";
      container.style.transition = "";
      container.style.position = "";
    }

    // 3. 重置所有面板
    this._panels.forEach((panel, i) => {
      // 移除所有動畫相關的 inline styles
      [
        "position",
        "top",
        "left",
        "width",
        "height",
        "zIndex",
        "pointerEvents",
        "opacity",
        "transition",
      ].forEach((p) => (panel.style[p] = ""));

      // 根據 keepVisibleIndex 決定顯示狀態
      if (keepVisibleIndex !== undefined) {
        if (i === keepVisibleIndex) {
          panel.hidden = false;
          panel.setAttribute("aria-hidden", "false");
        } else {
          panel.hidden = true;
          panel.setAttribute("aria-hidden", "true");
        }
      }
    });
  }

  // Fade 模式：Cross-fade (疊圖過渡) - 修正版
  async _showTabWithFade(index, previousIndex) {
    // 0. 強制重置之前的任何動畫狀態，確保起始狀態乾淨
    // 這解決了快速切換時圖片失真或消失的問題
    this._resetFade(previousIndex);

    const fromPanel = this._panels[previousIndex];
    const toPanel = this._panels[index];

    // 動畫參數配置
    // 圖片動畫：沿用舊的方式（panel 層級的 opacity）
    const imageDuration = 600;
    // 文字動畫：使用分離的方式（單獨的文字元素動畫）
    const textDuration = 300;
    const textEasing = "ease-in-out";

    // 使用當前 position 狀態而不是從屬性讀取
    this._updateCurrentPosition();
    const isBottomPosition = this._currentPosition === "bottom";

    // 選擇圖片和文字元素
    const fromImage = fromPanel?.querySelector(
      "before-after, .prose:first-child",
    );
    const fromText = fromPanel?.querySelector(".prose.responsive-tab-content");
    const toImage = toPanel?.querySelector("before-after, .prose:first-child");
    const toText = toPanel?.querySelector(".prose.responsive-tab-content");

    // 對於 bottom position，容器應該是 gallery-tab 元素本身
    // 對於其他 position，使用 panel 的父元素
    const container = isBottomPosition
      ? this.shadowRoot
        ? this
        : this._panels[0]?.closest("gallery-tab") ||
          this._panels[0]?.parentElement
      : fromPanel?.parentElement || this._panels[0]?.parentElement;
    if (!container) return;

    // 1. 準備容器
    // 確保容器是定位上下文，以便正確定位 fromPanel
    const originalContainerPosition = container.style.position || "";
    if (getComputedStyle(container).position === "static") {
      container.style.position = "relative";
    }

    // 2. 測量並鎖定 fromPanel（bottom position 需要特殊處理）
    let fromRect,
      containerRect,
      relativeTop,
      relativeLeft,
      startContainerHeight;

    if (isBottomPosition) {
      // 對於 bottom position，panel 使用 display: contents，需要從圖片元素獲取尺寸
      const fromImageRect = fromImage?.getBoundingClientRect();
      const fromTextRect = fromText?.getBoundingClientRect();

      containerRect = container.getBoundingClientRect();
      startContainerHeight = containerRect.height;

      // 準備 toPanel（先顯示以獲取正確尺寸）
      toPanel.hidden = false;
      toPanel.setAttribute("aria-hidden", "false");

      // 對於 bottom position，直接對圖片和文字元素進行動畫，不需要絕對定位 panel
      // 因為 panel 是 display: contents，它的子元素直接參與 grid
    } else {
      // 非 bottom position 的原有邏輯
      fromRect = fromPanel.getBoundingClientRect();
      containerRect = container.getBoundingClientRect();
      relativeTop = fromPanel.offsetTop;
      relativeLeft = fromPanel.offsetLeft;
      startContainerHeight = containerRect.height;

      // 從文檔流中移除 fromPanel，固定在當前視覺位置
      Object.assign(fromPanel.style, {
        position: "absolute",
        top: `${relativeTop}px`,
        left: `${relativeLeft}px`,
        width: `${fromRect.width}px`,
        height: `${fromRect.height}px`,
        zIndex: "1",
        pointerEvents: "none",
        opacity: "1",
      });

      // 3. 準備 toPanel
      toPanel.hidden = false;
      toPanel.setAttribute("aria-hidden", "false");
      Object.assign(toPanel.style, {
        opacity: "0",
        width: "100%", // 確保填滿可用空間
      });
    }

    // 4. 計算並動畫容器高度
    if (!isBottomPosition) {
      // 因為 toPanel 現在在文檔流中，容器高度會適應它
      // 我們需要強制瀏覽器重新計算佈局
      const endContainerHeight = container.scrollHeight; // 使用 scrollHeight 可能更準確
      const maxDuration = Math.max(imageDuration, textDuration);

      if (Math.abs(startContainerHeight - endContainerHeight) > 1) {
        container.style.overflow = "hidden";
        container.style.height = `${startContainerHeight}px`;
        container.style.transition = `height ${maxDuration}ms ease-in-out`;

        requestAnimationFrame(() => {
          container.style.height = `${endContainerHeight}px`;
        });
      }
    }
    // 對於 bottom position，容器高度由 grid 自動管理，不需要手動設置

    // 5. 準備元素初始狀態
    if (isBottomPosition) {
      // bottom position：直接對圖片和文字元素設置初始狀態
      if (fromImage) {
        fromImage.style.opacity = "1";
      }
      if (toImage) {
        toImage.style.opacity = "0";
      }
      if (fromText) {
        fromText.style.opacity = "1";
      }
      if (toText) {
        toText.style.opacity = "0";
      }
    } else {
      // 非 bottom position：準備文字元素的初始狀態
      // 圖片和面板本體沿用 panel 層級動畫，但文字單獨控制以確保與 bottom 一致
      if (fromText) {
        fromText.style.opacity = "1";
      }
      if (toText) {
        toText.style.opacity = "0";
      }
    }

    // 6. 執行動畫
    requestAnimationFrame(() => {
      if (isBottomPosition) {
        // bottom position：圖片直接對元素進行動畫
        if (fromImage) {
          fromImage.style.transition = `opacity ${imageDuration}ms ease-in-out`;
        }
        if (toImage) {
          toImage.style.transition = `opacity ${imageDuration}ms ease-in-out`;
        }

        // bottom position：文字動畫與 top 一致（使用 textEasing）
        if (fromText) {
          fromText.style.transition = `opacity ${textDuration}ms ${textEasing}`;
        }
        if (toText) {
          toText.style.transition = `opacity ${textDuration}ms ${textEasing}`;
        }
      } else {
        // 非 bottom position：圖片使用 panel 層級動畫
        fromPanel.style.transition = `opacity ${imageDuration}ms ease-in-out`;
        toPanel.style.transition = `opacity ${imageDuration}ms ease-in-out`;

        // 非 bottom position：文字使用元素層級動畫（使用 textEasing）
        if (fromText) {
          fromText.style.transition = `opacity ${textDuration}ms ${textEasing}`;
        }
        if (toText) {
          toText.style.transition = `opacity ${textDuration}ms ${textEasing}`;
        }
      }

      requestAnimationFrame(() => {
        // 執行圖片 fade
        if (isBottomPosition) {
          if (fromImage) {
            fromImage.style.opacity = "0";
          }
          if (toImage) {
            toImage.style.opacity = "1";
          }
        } else {
          fromPanel.style.opacity = "0";
          toPanel.style.opacity = "1";
        }

        // 執行文字 fade
        if (isBottomPosition) {
          // bottom position：序列式動畫（先消失再出現）
          if (fromText) {
            fromText.style.opacity = "0";
          }
          // 等待淡出完成後再淡入
          setTimeout(() => {
            if (toText) {
              toText.style.opacity = "1";
            }
          }, textDuration);
        } else {
          // 非 bottom position：cross-fade 動畫（同時淡入淡出）
          if (fromText) {
            fromText.style.opacity = "0";
          }
          if (toText) {
            toText.style.opacity = "1";
          }
        }
      });
    });

    // 7. 等待並清理
    return new Promise((resolve) => {
      // 使用最長的動畫時長來確保所有動畫都完成
      const cleanupDelay = Math.max(imageDuration, textDuration);

      this._fadeTimeout = setTimeout(() => {
        if (isBottomPosition) {
          // bottom position：清理圖片和文字元素
          if (fromImage) {
            ["opacity", "transition"].forEach((p) => (fromImage.style[p] = ""));
          }
          if (toImage) {
            ["opacity", "transition"].forEach((p) => (toImage.style[p] = ""));
          }
          if (fromText) {
            ["opacity", "transition"].forEach((p) => (fromText.style[p] = ""));
          }
          if (toText) {
            ["opacity", "transition"].forEach((p) => (toText.style[p] = ""));
          }

          // 清理 fromPanel
          fromPanel.hidden = true;
          fromPanel.setAttribute("aria-hidden", "true");
        } else {
          // 非 bottom position：清理 fromPanel
          fromPanel.hidden = true;
          fromPanel.setAttribute("aria-hidden", "true");
          [
            "position",
            "top",
            "left",
            "width",
            "height",
            "zIndex",
            "pointerEvents",
            "opacity",
            "transition",
          ].forEach((p) => (fromPanel.style[p] = ""));

          // 清理 fromPanel 中的文字元素（圖片沿用 panel 層級動畫，不需要單獨清理）
          if (fromText) {
            ["opacity", "transition"].forEach((p) => (fromText.style[p] = ""));
          }

          // 清理 toPanel
          ["opacity", "width", "transition"].forEach(
            (p) => (toPanel.style[p] = ""),
          );

          // 清理 toPanel 中的文字元素（圖片沿用 panel 層級動畫，不需要單獨清理）
          if (toText) {
            ["opacity", "transition"].forEach((p) => (toText.style[p] = ""));
          }

          // 清理容器
          container.style.height = "";
          container.style.overflow = "";
          container.style.transition = "";
        }

        container.style.position = originalContainerPosition;
        this._fadeTimeout = null;
        resolve();
      }, cleanupDelay);
    });
  }
  // Slide 模式：滑動 + 淡入淡出動畫（模擬 x-tabs 原始動畫）
  async _showTabWithSlide(index, previousIndex) {
    const fromPanel = this._panels[previousIndex];
    const toPanel = this._panels[index];

    // 使用當前 position 狀態而不是從屬性讀取
    this._updateCurrentPosition();
    const isBottomPosition = this._currentPosition === "bottom";

    // 選擇圖片和文字元素
    const fromImage = fromPanel?.querySelector(
      "before-after, .prose:first-child",
    );
    const fromText = fromPanel?.querySelector(".prose.responsive-tab-content");
    const toImage = toPanel?.querySelector("before-after, .prose:first-child");
    const toText = toPanel?.querySelector(".prose.responsive-tab-content");

    // 1. 立即清理所有面板上的現有動畫
    this._panels.forEach((p) =>
      p.getAnimations().forEach((anim) => anim.cancel()),
    );
    // 清理圖片和文字元素的動畫
    [fromImage, toImage, fromText, toText].forEach((el) => {
      if (el) el.getAnimations().forEach((anim) => anim.cancel());
    });

    if (isBottomPosition) {
      // bottom position：直接對圖片和文字元素進行 slide + fade 動畫

      // 2. 如果有舊元素且可見，先執行滑動淡出
      const fadeOutPromises = [];

      if (fromImage && !fromPanel.hidden) {
        fromImage.style.opacity = "1";
        fromImage.style.transform = "translateY(0)";
        fadeOutPromises.push(
          fromImage.animate(
            [
              { opacity: 1, transform: "translateY(0)" },
              { opacity: 0, transform: "translateY(-20px)" },
            ],
            {
              duration: 300,
              easing: "ease-in-out",
            },
          ).finished,
        );
      }

      if (fromText && !fromPanel.hidden) {
        fromText.style.opacity = "1";
        fromText.style.transform = "translateY(0)";
        fadeOutPromises.push(
          fromText.animate(
            [
              { opacity: 1, transform: "translateY(0)" },
              { opacity: 0, transform: "translateY(-20px)" },
            ],
            {
              duration: 300,
              easing: "ease-in-out",
            },
          ).finished,
        );
      }

      // 等待所有淡出動畫完成
      await Promise.all(fadeOutPromises);

      // 動畫結束後隱藏 panel
      fromPanel.hidden = true;
      fromPanel.setAttribute("aria-hidden", "true");
      if (fromImage) {
        fromImage.style.opacity = "";
        fromImage.style.transform = "";
      }
      if (fromText) {
        fromText.style.opacity = "";
        fromText.style.transform = "";
      }

      // 3. 準備並顯示新面板
      toPanel.hidden = false;
      toPanel.setAttribute("aria-hidden", "false");

      // 設置初始狀態
      if (toImage) {
        toImage.style.opacity = "0";
        toImage.style.transform = "translateY(20px)";
      }
      if (toText) {
        toText.style.opacity = "0";
        toText.style.transform = "translateY(20px)";
      }

      // 4. 執行滑動淡入
      const fadeInPromises = [];

      if (toImage) {
        fadeInPromises.push(
          toImage.animate(
            [
              { opacity: 0, transform: "translateY(20px)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            {
              duration: 300,
              easing: "ease-in-out",
            },
          ).finished,
        );
      }

      if (toText) {
        fadeInPromises.push(
          toText.animate(
            [
              { opacity: 0, transform: "translateY(20px)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            {
              duration: 300,
              easing: "ease-in-out",
            },
          ).finished,
        );
      }

      await Promise.all(fadeInPromises);

      // 5. 最終狀態
      if (toImage) {
        toImage.style.opacity = "1";
        toImage.style.transform = "translateY(0)";
      }
      if (toText) {
        toText.style.opacity = "1";
        toText.style.transform = "translateY(0)";
      }
    } else {
      // 非 bottom position：原有邏輯（對 panel 進行動畫）

      // 2. 如果有舊面板且可見，先執行滑動淡出
      if (fromPanel && !fromPanel.hidden) {
        // 確保樣式正確
        fromPanel.style.opacity = "1";
        fromPanel.style.transform = "translateY(0)";

        await fromPanel.animate(
          [
            { opacity: 1, transform: "translateY(0)" },
            { opacity: 0, transform: "translateY(-20px)" },
          ],
          {
            duration: 300,
            easing: "ease-in-out",
          },
        ).finished;

        // 動畫結束後隱藏
        fromPanel.hidden = true;
        fromPanel.style.display = "none";
        fromPanel.style.transform = "";
      } else if (fromPanel) {
        fromPanel.hidden = true;
        fromPanel.style.display = "none";
      }

      // 3. 準備並顯示新面板
      toPanel.hidden = false;
      toPanel.style.display = "";
      toPanel.style.opacity = "0";
      toPanel.style.transform = "translateY(20px)";

      // 4. 執行滑動淡入
      await toPanel.animate(
        [
          { opacity: 0, transform: "translateY(20px)" },
          { opacity: 1, transform: "translateY(0)" },
        ],
        {
          duration: 300,
          easing: "ease-in-out",
        },
      ).finished;

      // 5. 最終狀態
      toPanel.style.opacity = "1";
      toPanel.style.transform = "translateY(0)";
    }
  }

  _fallbackInitialization() {
    this._buttons = Array.from(this.querySelectorAll('button, [role="tab"]'));
    this._panels = Array.from(
      this.querySelectorAll('[role="tabpanel"], .tab-panel'),
    );
    this._animationMode = this.getAttribute("data-animation-mode") || "none";
    this._selectedIndex = 0;

    // 基本事件設置
    this._buttons.forEach((button, index) => {
      button.addEventListener("click", () => (this.selectedIndex = index));
    });

    // 清除所有面板的狀態，確保乾淨的初始狀態
    this._panels.forEach((panel) => {
      panel.style.transform = "";
      panel.style.opacity = "";
    });

    this._showTab(0, true);
    this._isInitialized = true;
  }

  // 屬性觀察
  static get observedAttributes() {
    return ["selected-index"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (
      name === "selected-index" &&
      oldValue !== newValue &&
      this._isInitialized
    ) {
      const newIndex = parseInt(newValue) || 0;
      if (
        newIndex !== this._selectedIndex &&
        newIndex >= 0 &&
        newIndex < this._buttons.length
      ) {
        this._showTab(newIndex);
      }
    }
  }

  // Getter 和 Setter
  get selectedIndex() {
    return this._selectedIndex;
  }

  set selectedIndex(index) {
    const validIndex = Math.min(Math.max(index, 0), this._buttons.length - 1);
    this.setAttribute("selected-index", validIndex.toString());
  }

  // 為了兼容性，提供一些 x-tabs 可能有的方法
  get buttons() {
    return this._buttons;
  }

  get panels() {
    return this._panels;
  }

  // 模擬 x-tabs 的 API（如果現有 JavaScript 需要的話）
  querySelectorAll(selectors) {
    return super.querySelectorAll(selectors);
  }

  querySelector(selector) {
    return super.querySelector(selector);
  }
}

// 註冊 gallery-tab 自定義元素
if (!window.customElements.get("gallery-tab")) {
  window.customElements.define("gallery-tab", GalleryTab);
}

// 初始化已存在的 gallery-tab 元素
function initializeExistingGalleryTabs() {
  const existingTabs = document.querySelectorAll("gallery-tab");
  existingTabs.forEach((tab) => {
    if (!tab._isInitialized) {
      tab.connectedCallback();
    }
  });
}

// 在 DOM 準備就緒時初始化
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeExistingGalleryTabs);
} else {
  initializeExistingGalleryTabs();
}

// 為 Shopify 動態內容提供支持
document.addEventListener(
  "shopify:section:load",
  initializeExistingGalleryTabs,
);
document.addEventListener(
  "shopify:block:select",
  initializeExistingGalleryTabs,
);

/**
 * =============================================================================
 * COPY TO CLIPBOARD UTILITY - Universal Copy Button Enhancement
 * =============================================================================
 *
 * A universal utility for copy-to-clipboard functionality that works with
 * any button or element that has data attributes for the content to copy.
 *
 * SUPPORTED DATA ATTRIBUTES:
 * - data-copy-text: Copy plain text
 * - data-copy-email: Copy email address
 * - data-copy-link: Copy URL (defaults to current page URL if not specified)
 * - data-copy-success: Custom success message (defaults to "Copied")
 * - data-copy-duration: Duration to show success message in ms (defaults to 1000)
 *
 * USAGE:
 * <button data-copy-email="user@example.com">Copy Email</button>
 * <button data-copy-link="https://example.com">Copy Link</button>
 * <button data-copy-text="Some text">Copy Text</button>
 *
 * =============================================================================
 */

/**
 * Copy text to clipboard with fallback support
 */
async function copyToClipboard(text) {
  if (!text) return false;

  // Try modern Clipboard API first
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // Fall through to the execCommand fallback below.
    }
  }

  // Fallback for older browsers
  try {
    const textArea = document.createElement("textarea");
    Object.assign(textArea.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "1px",
      height: "1px",
      opacity: "0",
      padding: "0",
      border: "none",
      outline: "none",
    });
    textArea.value = text;
    textArea.setAttribute("readonly", "");

    document.body.appendChild(textArea);
    textArea.select();
    textArea.setSelectionRange(0, text.length);

    const success = document.execCommand("copy");
    document.body.removeChild(textArea);
    return success;
  } catch (err) {
    return false;
  }
}

/**
 * Show success feedback on element
 * Works with any element type (button, a, div, etc.)
 * Uses actual DOM element for tooltip to avoid conflicts with existing ::after pseudo-elements
 */
function showCopySuccess(element, message, duration) {
  // Remove any existing tooltip first
  const existingTooltip = element.querySelector(".copy-tooltip");
  if (existingTooltip) {
    existingTooltip.remove();
  }

  // Create tooltip element
  const tooltip = document.createElement("div");
  tooltip.className = "copy-tooltip";
  tooltip.textContent = message || "Copied";

  // Append to element
  element.appendChild(tooltip);

  // Trigger animation by adding active class
  requestAnimationFrame(() => {
    tooltip.classList.add("active");
  });

  // Remove tooltip after duration
  setTimeout(() => {
    tooltip.classList.remove("active");
    // Wait for transition to complete before removing from DOM
    setTimeout(() => {
      if (tooltip.parentNode) {
        tooltip.remove();
      }
    }, 200); // Match CSS transition duration
  }, duration);
}

/**
 * Initialize copy buttons with progressive enhancement
 */
function initializeCopyButtons() {
  // Wait for DOM to be fully loaded
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeCopyButtons);
    return;
  }

  // Find all elements with copy data attributes (works with any element type)
  const copyElements = document.querySelectorAll(
    "[data-copy-text], [data-copy-email], [data-copy-link]",
  );

  copyElements.forEach((element) => {
    // Skip if already initialized
    if (element.hasAttribute("data-copy-initialized")) {
      return;
    }

    // Mark as initialized
    element.setAttribute("data-copy-initialized", "true");

    // Ensure element is clickable (add cursor pointer if not already styled)
    if (getComputedStyle(element).cursor === "auto") {
      element.style.cursor = "pointer";
    }

    // Add click event listener
    element.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Determine what to copy
      let textToCopy = "";
      if (element.hasAttribute("data-copy-email")) {
        textToCopy = element.getAttribute("data-copy-email");
      } else if (element.hasAttribute("data-copy-link")) {
        const link = element.getAttribute("data-copy-link");
        textToCopy = link || window.location.href;
      } else if (element.hasAttribute("data-copy-text")) {
        textToCopy = element.getAttribute("data-copy-text");
      }

      if (!textToCopy) return;

      // Copy to clipboard
      const success = await copyToClipboard(textToCopy);

      if (element.closest('[data-wo-track-section="compare-products-custom"]')) {
        try { window.WOSectionTracking?.track(element, "copy_result", { outcome: success ? "success" : "error" }); } catch (_) {}
      }

      if (success) {
        // Get success message and duration
        // Use existing data-copy-success if present, otherwise default to "Copied"
        const successMessage =
          element.getAttribute("data-copy-success") || "Copied";
        const duration =
          parseInt(element.getAttribute("data-copy-duration")) || 1000;

        // Show success feedback (only sets attribute if not already present)
        showCopySuccess(element, successMessage, duration);
      }
    });
  });
}

// Initialize on page load
initializeCopyButtons();

// Re-initialize when new content is loaded (for dynamic content)
document.addEventListener("shopify:section:load", initializeCopyButtons);
document.addEventListener("shopify:block:load", initializeCopyButtons);

/**
 * =============================================================================
 * DESKTOP IMAGE SCROLL FIX
 * =============================================================================
 * Ensures that on desktop, sections with "no image" blocks display as blank/empty
 * instead of persisting the previous block's image.
 */
/**
 * =============================================================================
 * DESKTOP IMAGE PERSISTENCE FIX (Phantom Image Strategy)
 * =============================================================================
 * Instead of fighting the theme's "sticky" behavior, we inject an invisible
 * placeholder image into text-only blocks. This tricks the theme into treating
 * them as normal image blocks, causing it to "switch" to the invisible image
 * (clearing the previous one) rather than keeping the old one stuck.
 */
function injectPhantomImages() {
  // Only needed on desktop
  if (!window.matchMedia("(min-width: 1001px)").matches) return;

  const sections = document.querySelectorAll("images-with-text-scroll");

  sections.forEach((section) => {
    const items = section.querySelectorAll(".images-with-text-scroll__item");

    items.forEach((item) => {
      // Check if this item already has a real image
      // Note: We exclude our own phantom images to avoid double injection
      const existingImage = item.querySelector(
        "img.images-with-text-scroll__image:not(.phantom-image)",
      );

      if (!existingImage) {
        // Check if we already injected one
        if (item.querySelector(".phantom-image")) return;

        // Create a 1x1 transparent spacer GIF
        const phantom = document.createElement("img");
        phantom.src =
          "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
        phantom.classList.add(
          "images-with-text-scroll__image",
          "phantom-image",
        );
        phantom.alt = "";

        // Styling to make it invisible and non-intrusive
        phantom.style.opacity = "0";
        phantom.style.visibility = "hidden";
        phantom.style.position = "absolute"; // Take it out of flow to prevent layout shifts
        phantom.style.width = "1px";
        phantom.style.height = "1px";
        phantom.style.pointerEvents = "none";

        // Insert at the beginning of the item (where images usually are)
        item.prepend(phantom);

        // IMPORTANT: Add the class that marks this item as having an image
        // This ensures any CSS/JS logic targeting ".has-image" items will apply here too
        item.classList.add("has-image");
      }
    });
  });
}

// Execute immediately and on Shopify editor events
injectPhantomImages();
document.addEventListener("shopify:section:load", injectPhantomImages);
class ProductConfigurator extends HTMLElement {
  constructor() {
    super();
    this.state = {
      selectedAddons: new Map(),
      activeTab: "imaging",
      activeCategory: null,
      basePrice: 0,
      addonsPrice: 0,
      bundleIncludedIds: new Set(),
    };
    this._categoryPairs = {
      tripod: "tripod-visual",
      "tripod-visual": "tripod",
      "mounting-plate": "mounting-plate-visual",
      "mounting-plate-visual": "mounting-plate",
    };
  }

  connectedCallback() {
    this._abortController = new AbortController();
    const signal = this._abortController.signal;
    this.state.basePrice = parseInt(this.dataset.variantPrice, 10) || 0;
    this._modal = this._find("[data-configurator-modal]");

    this.loadBundleIncludedIds();
    this.bindEvents();
    this.bindVariantPicker();
    this.initTabs();
    this.restoreOptionFromStorage();
    this.syncOptionState();

    // bfcache: connectedCallback won't re-fire, so re-sync on pageshow
    window.addEventListener(
      "pageshow",
      (e) => {
        if (e.persisted) {
          // bfcache already restores visual state (checkboxes, form values).
          // syncOptionState would clear checkboxes — skip it.
          // Just rebuild JS state from the restored DOM.
          this.rebuildSelectionsFromDom();
        }
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._scrollSpyCleanup?.();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: DOM Helpers
  // Modal-aware querySelector/querySelectorAll — search `this` first;
  // when modal is detached (appended to body), also search this._modal.
  // ═══════════════════════════════════════════════════════════════════════════

  _find(sel) {
    const hit = HTMLElement.prototype.querySelector.call(this, sel);
    return (
      hit ??
      (this._modal && !this.contains(this._modal)
        ? this._modal.querySelector(sel)
        : null)
    );
  }

  _findAll(sel) {
    const results = Array.from(
      HTMLElement.prototype.querySelectorAll.call(this, sel),
    );
    if (this._modal && !this.contains(this._modal)) {
      results.push(...this._modal.querySelectorAll(sel));
    }
    return results;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Initialization & State Sync
  // Handles back-navigation / bfcache restoration, initial option state.
  // ═══════════════════════════════════════════════════════════════════════════

  syncOptionState() {
    // 1. Reset all user-selected addon checkboxes and state
    this.state.selectedAddons.clear();
    this._findAll(".wo-checkbox[data-category]").forEach((input) => {
      if (input.dataset.bundleIncluded !== "true") {
        input.checked = false;
      }
      const card = input.closest(".cfg-product");
      if (card) card.classList.remove("is-selected");
    });

    // 2. Clear any bundle styling from a previous session
    this.clearBundleInclusions();
    this.loadBundleIncludedIds();

    // 3. Determine the currently checked option
    const checkedOption = this._find(
      'input[name="configurator-option"]:checked',
    );
    const hasBundleRadio = !!this._find(
      'input[name="configurator-option"][data-option-type="bundle"]',
    );

    // 4. Sync .cfg-option--selected class and hidden ID to match the :checked radio
    if (checkedOption) {
      this._findAll(".cfg-option").forEach((opt) => {
        opt.classList.toggle(
          "cfg-option--selected",
          opt.contains(checkedOption),
        );
      });
      const optionPrice = parseInt(checkedOption.dataset.price, 10) || 0;
      this.state.basePrice = this.getPromoPrice(optionPrice, checkedOption);
      const mainIdInput = this._find("[data-configurator-main-id]");
      if (mainIdInput) mainIdInput.value = checkedOption.value;
    }

    // 5. Apply bundle inclusions if needed
    const mainPickerWrapper = this._find("[data-main-variant-picker]");
    const variantPicker = mainPickerWrapper
      ? mainPickerWrapper.querySelector("variant-picker")
      : this._find("variant-picker");
    if (this.state.bundleIncludedIds.size > 0 && !hasBundleRadio) {
      // Case 1: product IS a bundle (IDs from loadBundleIncludedIds)
      this.applyBundleInclusions();
    } else if (checkedOption?.dataset.optionType === "bundle") {
      // Case 2: user selected a bundle option — load IDs from radio attribute
      this.activateBundleOption(checkedOption);
    } else {
      if (mainPickerWrapper) mainPickerWrapper.hidden = false;
      // Hide all bundle pickers
      this._findAll("[data-bundle-picker-for]").forEach((w) => {
        w.hidden = true;
      });
      this._activeBundlePicker = null;
    }

    // 6. Ensure variant-picker defaults to first available variant
    //    (skip if activateBundleOption already handled it above)
    if (!this._activeBundlePicker && variantPicker) {
      let variant = this.autoSelectFirstAvailable(variantPicker);
      this.updateBuyButtonState(variant);
    } else if (!this._activeBundlePicker && !variantPicker && checkedOption) {
      // No variant-picker (e.g. bundle product with default variant only)
      this.updateBuyButtonState({ id: checkedOption.value, available: true });
    }

    // 7. Toggle bundle-only cards and discount prices by selected option (main vs specific bundle)
    const selectedOptionProductId =
      checkedOption?.dataset.optionProductId != null
        ? String(checkedOption.dataset.optionProductId)
        : null;
    const isBundle =
      (this.state.bundleIncludedIds.size > 0 && !hasBundleRadio) ||
      checkedOption?.dataset.optionType === "bundle";
    this.toggleBundleOnlyCards(selectedOptionProductId, isBundle);
    this.updateDiscountPrices(selectedOptionProductId);

    this.registerFreeItems();
    this.restoreSelectionState();
    this.updateCategoryBadges();
    this.updatePrices();

    // 8. Fix image mismatch: server may render a sold-out variant's image
    // while the <select> defaults to the first available variant
    this.syncAddonCardImages();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Bundle Option Management
  // Buy button state, bundle option activation, variant selection helpers.
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Sync the Add-to-Cart (modal) and outer BUY NOW buttons with variant availability.
   * Called from syncOptionState, onVariantSwitch, and interceptRerender.
   * @param {Object|null} variant  – variant JSON object, or null when no variant matches
   */
  updateBuyButtonState(variant) {
    // variant === null  → combination doesn't exist → "Unavailable"
    // variant.available === false → exists but sold out → "Sold out"
    // variant.available === true → in stock → normal button text
    const submitBtn = this._find("[data-configurator-submit-wrapper] button");
    if (submitBtn) {
      const wrapper = submitBtn.closest("[data-configurator-submit-wrapper]");
      const labelAddToCart = wrapper?.dataset.labelAddToCart || "Add to Cart";
      const labelSoldOut = wrapper?.dataset.labelSoldOut || "Sold out";
      const labelUnavailable =
        wrapper?.dataset.labelUnavailable || "Unavailable";

      if (variant === null || variant === undefined) {
        submitBtn.textContent = labelUnavailable;
        submitBtn.disabled = true;
        submitBtn.classList.add("is-sold-out");
      } else if (!variant.available) {
        submitBtn.textContent = labelSoldOut;
        submitBtn.disabled = true;
        submitBtn.classList.add("is-sold-out");
      } else {
        submitBtn.textContent = labelAddToCart;
        submitBtn.disabled = false;
        submitBtn.classList.remove("is-sold-out");
      }
    }

    const formId =
      this._outerFormId ||
      this._innerPicker?.getAttribute("form-id") ||
      this._find("variant-picker")?.getAttribute("form-id");
    if (!formId) return;

    const outerBuyButtons = document.querySelectorAll(
      `buy-buttons[form="${formId}"] button[data-configurator-open]`,
    );
    outerBuyButtons.forEach((btn) => {
      if (!btn.dataset.originalText) {
        btn.dataset.originalText = btn.textContent.trim();
      }
      if (variant === null || variant === undefined) {
        btn.textContent = "Unavailable";
        btn.disabled = true;
        btn.classList.add("is-sold-out");
      } else if (!variant.available) {
        btn.textContent = "Sold out";
        btn.disabled = true;
        btn.classList.add("is-sold-out");
      } else {
        btn.textContent = btn.dataset.originalText || "BUY NOW";
        btn.disabled = false;
        btn.classList.remove("is-sold-out");
      }
    });
  }

  /**
   * Activate a bundle radio option: hide variant picker, parse included variant
   * IDs from CSV, and apply bundle inclusions.
   * @param {HTMLInputElement} radio – the checked bundle radio input
   */
  activateBundleOption(radio) {
    const mainPickerWrapper = this._find("[data-main-variant-picker]");
    const bundleProductId = radio.dataset.bundleProductId;

    // Hide main picker
    if (mainPickerWrapper) mainPickerWrapper.hidden = true;

    // Hide all bundle pickers, then show the one matching this radio
    this._findAll("[data-bundle-picker-for]").forEach((w) => {
      w.hidden = true;
    });

    const bundlePickerWrapper = bundleProductId
      ? this._find(`[data-bundle-picker-for="${CSS.escape(bundleProductId)}"]`)
      : null;

    if (bundlePickerWrapper) {
      bundlePickerWrapper.hidden = false;
      this._activeBundlePicker =
        bundlePickerWrapper.querySelector("variant-picker");

      // Switch variant resolution to the bundle product's variants
      const variants = this._bundleVariantsCache?.[bundleProductId];
      if (variants) {
        this._productVariants = variants;
      }

      // Ensure default variant options are selected (the theme's VariantPicker
      // custom element may uncheck radios when the picker transitions from
      // hidden to visible or when form-id is removed).
      if (this._activeBundlePicker && variants) {
        const defaultVariant = variants.find((v) => v.available);
        if (defaultVariant) {
          this.selectVariantByOptions(
            this._activeBundlePicker,
            defaultVariant.options,
          );
        }
        this.updateBundleOptionAvailability(this._activeBundlePicker, variants);
        this.resolveVariantFromPicker(this._activeBundlePicker);
      }
    } else {
      // Fallback: no bundle picker rendered (e.g. bundle product on its own page)
      this._activeBundlePicker = null;
      const variantPicker = this._find(
        "[data-main-variant-picker] variant-picker",
      );

      // Force variant picker to bundle's option values (e.g. "Red")
      const bundleOptions = (radio.dataset.bundleVariantOptions || "")
        .split("|")
        .map((s) => s.trim())
        .filter(Boolean);
      if (bundleOptions.length > 0 && variantPicker) {
        this.selectVariantByOptions(variantPicker, bundleOptions);
      }
      if (mainPickerWrapper) mainPickerWrapper.hidden = true;
    }

    const variantsCsv = radio.dataset.bundleIncludedVariants || "";
    if (variantsCsv) {
      this.state.bundleIncludedIds = new Set(
        variantsCsv.split(",").map((id) => String(id.trim())),
      );
      this.applyBundleInclusions();
    }
  }

  /**
   * Force-select variant picker radios to match the given option texts.
   * Used when switching to a bundle to reset the main product attributes.
   */
  selectVariantByOptions(variantPicker, targetOptions) {
    const fieldsets = variantPicker.querySelectorAll("fieldset");
    fieldsets.forEach((fieldset, idx) => {
      const targetText = targetOptions[idx];
      if (!targetText) return;

      const radios = fieldset.querySelectorAll('input[type="radio"]');
      for (const radio of radios) {
        const label = radio.id
          ? variantPicker.querySelector(`label[for="${CSS.escape(radio.id)}"]`)
          : null;
        const labelText = label
          ? label.textContent.trim()
          : radio.closest("label")?.textContent.trim() || "";

        if (labelText === targetText && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
          break;
        }
      }
    });
  }

  isVariantDisabled(input) {
    if (!input) return false;
    // Find the associated label: either an ancestor (dropdown type) or a sibling via for attr (block/swatch type)
    const forLabel = input.id
      ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`)
      : null;
    return (
      input.disabled ||
      input.classList.contains("is-disabled") ||
      input.closest("label")?.classList.contains("is-disabled") ||
      forLabel?.classList.contains("is-disabled") ||
      input.nextElementSibling?.textContent.includes("Sold out")
    );
  }

  formatMoney(cents, withCurrency = true) {
    const amount = (cents / 100).toFixed(2);
    const format = withCurrency && window.themeVariables?.settings?.currencyCodeEnabled
      ? window.themeVariables?.settings?.moneyWithCurrencyFormat
      : window.themeVariables?.settings?.moneyFormat;
    if (format) {
      return format
        .replace("{{amount}}", amount)
        .replace("{{amount_with_comma_separator}}", amount.replace(".", ","))
        .replace("{{amount_no_decimals}}", Math.round(cents / 100));
    }
    return `$${amount} USD`;
  }

  getPromoPrice(price, optionRadio) {
    const priceList = optionRadio
      ?.closest(".cfg-option")
      ?.querySelector(".price-list--promo-price");
    const promoData =
      priceList ||
      optionRadio
        ?.closest(".cfg-product")
        ?.querySelector("[data-configurator-promo]");
    if (!promoData) return price;

    const starts =
      (parseInt(promoData.dataset.promoPriceStarts, 10) || 0) * 1000;
    const ends = (parseInt(promoData.dataset.promoPriceEnds, 10) || 0) * 1000;
    const now = Date.now();
    if ((starts > 0 && now < starts) || (ends > 0 && now >= ends)) {
      return price;
    }

    const percent = parseInt(promoData.dataset.promoPricePercent, 10) || 0;
    return price - Math.floor((price * percent) / 100);
  }

  updateConfiguratorOptionPrice(variant, optionRadio) {
    const priceList = optionRadio
      ?.closest(".cfg-option")
      ?.querySelector(".price-list");
    const salePrice = priceList?.querySelector("sale-price");
    if (!salePrice) return;

    const price = parseInt(variant.price, 10) || 0;
    const displayedPrice = this.getPromoPrice(price, optionRadio);
    const compareAtPrice = parseInt(variant.compare_at_price, 10) || 0;
    const strikePrice = Math.max(
      compareAtPrice > price ? compareAtPrice : 0,
      displayedPrice < price ? price : 0,
    );
    const salePriceSr = salePrice.querySelector(".sr-only");
    salePrice.textContent = this.formatMoney(displayedPrice);
    if (salePriceSr) salePrice.prepend(salePriceSr);
    salePrice.classList.toggle("text-on-sale", strikePrice > displayedPrice);
    salePrice.classList.toggle("text-subdued", strikePrice <= displayedPrice);

    const compareAt = priceList.querySelector("compare-at-price");
    if (compareAt) {
      compareAt.hidden = strikePrice <= displayedPrice;
      if (!compareAt.hidden) {
        const compareAtSr = compareAt.querySelector(".sr-only");
        compareAt.textContent = this.formatMoney(strikePrice, false);
        if (compareAtSr) compareAt.prepend(compareAtSr);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Variant Picker Binding & Sync
  // Inner/outer picker coordination, bundle picker detach, option availability.
  // ═══════════════════════════════════════════════════════════════════════════

  bindVariantPicker() {
    const mainWrapper = this._find("[data-main-variant-picker]");
    const picker = mainWrapper
      ? mainWrapper.querySelector("variant-picker")
      : this._find("variant-picker");
    if (!picker) return;

    const formId = picker.getAttribute("form-id");
    const form = formId && document.getElementById(formId);
    if (!form) return;

    this._outerForm = form;
    this._outerFormId = formId;
    this._innerPicker = picker;

    // Both pickers share the same name+form — browser only keeps
    // the LAST radio checked per group, so the outer loses its state.
    // Detach inner from form first, then restore outer checked state.
    this.detachInnerPickerFromForm(picker);
    this.restoreOuterCheckedState(picker, form);

    this.syncInnerToOuter(picker, form);
    this.interceptRerender(form);
    this.syncOuterToInner(picker, form);

    // Bind bundle variant pickers (detach from form, listen for changes)
    this.bindBundlePickers();
  }

  /**
   * Detach bundle pickers from the form (prevent theme VariantPicker AJAX which
   * fails because the AJAX response won't contain our custom form-id).
   * Instead, handle variant resolution and option availability manually.
   */
  bindBundlePickers() {
    const signal = this._abortController.signal;
    this._bundleVariantsCache = {};

    this._findAll("[data-bundle-picker-for]").forEach((wrapper) => {
      const bundlePicker = wrapper.querySelector("variant-picker");
      if (!bundlePicker) return;

      const bundleProductId = wrapper.dataset.bundlePickerFor;

      // Detach from form to prevent theme VariantPicker AJAX (it will crash)
      this.detachInnerPickerFromForm(bundlePicker);

      // Pre-parse and cache bundle variants JSON
      const variantsScript = this._find(
        `[data-bundle-product-variants="${CSS.escape(bundleProductId)}"]`,
      );
      if (variantsScript) {
        try {
          this._bundleVariantsCache[bundleProductId] = JSON.parse(
            variantsScript.textContent,
          );
        } catch (_) {}
      }
      // Listen for changes: resolve variant + update availability manually
      bundlePicker.addEventListener(
        "change",
        (e) => {
          if (e.target.type !== "radio") return;
          this.updatePickerOptionLabel(bundlePicker, e.target);

          const variants = this._bundleVariantsCache[bundleProductId];
          if (variants) {
            this.updateBundleOptionAvailability(bundlePicker, variants, 0, e.target);
            this._productVariants = variants;
            this.resolveVariantFromPicker(bundlePicker);
          }
        },
        { signal },
      );    });
  }

  /**
   * Update is-disabled state on bundle picker option labels based on available
   * variant combinations. Replicates Shopify's linked-option availability logic.
   * When a selected option becomes disabled, automatically switches to the first
   * available option and cascades the availability update.
   */
  updateBundleOptionAvailability(picker, variants, _depth = 0, changedTarget = null) {
    if (_depth > 5) return;
    const fieldsets = picker.querySelectorAll(
      "fieldset.variant-picker__option",
    );

    // Helper: get the text of a radio from its label
    const getRadioText = (radio) => {
      const label = radio.id
        ? picker.querySelector(`label[for="${CSS.escape(radio.id)}"]`)
        : null;
      return label
        ? (
            label.querySelector(".sr-only")?.textContent || label.textContent
          ).trim()
        : "";
    };

    // Collect currently selected option texts
    const selectedTexts = [];
    fieldsets.forEach((fs) => {
      const checked = fs.querySelector('input[type="radio"]:checked');
      selectedTexts.push(checked ? getRadioText(checked) : "");
    });

    // For each option position, update availability and auto-select if needed
    let changed = false;
    fieldsets.forEach((fs, optionIndex) => {
      let selectedIsAvailable = false;

      fs.querySelectorAll('input[type="radio"]').forEach((radio) => {
        const label = radio.id
          ? picker.querySelector(`label[for="${CSS.escape(radio.id)}"]`)
          : null;
        if (!label) return;

        const radioText = getRadioText(radio);

        // A value is available if there exists a variant where:
        // - All options BEFORE this position match the currently selected values
        // - This position matches the candidate value
        // - The variant is available (in stock)
        const isAvailable = variants.some(
          (v) =>
            v.available &&
            v.options[optionIndex] === radioText &&
            v.options.every(
              (opt, idx) => idx >= optionIndex || opt === selectedTexts[idx],
            ),
        );

        label.classList.toggle("is-disabled", !isAvailable);

        if (radio.checked && isAvailable) {
          selectedIsAvailable = true;
        }
      });

      // Determine if we should perform auto-correction on this fieldset:
      let shouldAutoCorrect = true;
      if (changedTarget) {
        const changedFieldset = changedTarget.closest("fieldset.variant-picker__option");
        const changedLegendText = changedFieldset?.querySelector("legend")?.textContent || "";
        const changedIsColor = /color|顏色/i.test(changedLegendText);

        if (!changedIsColor) {
          // If user clicked a non-color option (e.g. size), do not perform auto-correction
          shouldAutoCorrect = false;
        } else {
          // If user clicked a color option, do not auto-correct the color option itself
          if (fs === changedFieldset) {
            shouldAutoCorrect = false;
          }
        }
      }

      // If the currently selected option in this fieldset is disabled,
      // auto-select the first available option
      if (!selectedIsAvailable && shouldAutoCorrect) {
        const radios = fs.querySelectorAll('input[type="radio"]');
        for (const radio of radios) {
          const label = radio.id
            ? picker.querySelector(`label[for="${CSS.escape(radio.id)}"]`)
            : null;
          if (label && !label.classList.contains("is-disabled")) {
            radio.checked = true;
            selectedTexts[optionIndex] = getRadioText(radio);
            this.updatePickerOptionLabel(picker, radio);
            changed = true;
            break;
          }
        }
      }
    });

    // If any auto-selection happened, re-run availability for cascading updates
    if (changed) {
      this.updateBundleOptionAvailability(picker, variants, _depth + 1, changedTarget);
    }
  }

  /**
   * Remove `form` attribute from inner picker radios and clear its `form-id`
   * so the theme's variant-picker handler can't trigger section re-renders.
   * This breaks the radio mutual-exclusion with the outer picker.
   */
  detachInnerPickerFromForm(picker) {
    // Tear down the inner VariantPicker's delegate listener to prevent it
    // from also handling change events meant for the outer picker.
    if (typeof picker.disconnectedCallback === "function") {
      picker.disconnectedCallback();
    }

    picker.removeAttribute("form-id");
    picker.querySelectorAll('input[type="radio"][form]').forEach((radio) => {
      radio.removeAttribute("form");
    });
    this.deduplicatePickerIds(picker);
  }

  /**
   * Prefix radio IDs inside a picker with "cfg-" and update corresponding
   * label[for] attributes so that they target the inner radio instead of
   * the outer one (which shares the same original ID).
   */
  deduplicatePickerIds(picker) {
    picker.querySelectorAll("input[type='radio'][id]").forEach((radio) => {
      const oldId = radio.id;
      if (oldId.startsWith("cfg-")) return;
      const newId = "cfg-" + oldId;
      const label = picker.querySelector(`label[for="${CSS.escape(oldId)}"]`);
      if (label) label.setAttribute("for", newId);
      radio.id = newId;
    });
  }

  /**
   * After detaching inner picker, the outer radios may have lost their
   * checked state (browser deduplication). Restore by mirroring inner
   * picker's checked radios to the outer form.
   */
  restoreOuterCheckedState(picker, form) {
    const formId = form.getAttribute("id");
    if (!formId) return;

    const checkedInner = picker.querySelectorAll('input[type="radio"]:checked');
    checkedInner.forEach((innerRadio) => {
      const sel = `input[type="radio"][form="${CSS.escape(formId)}"][name="${CSS.escape(innerRadio.name)}"][value="${CSS.escape(innerRadio.value)}"]`;
      const outerRadio = document.querySelector(sel);
      if (outerRadio && !outerRadio.checked) {
        outerRadio.checked = true;
      }
    });
  }

  syncInnerToOuter(picker, form) {
    const signal = this._abortController.signal;
    const formId = form.getAttribute("id");

    picker.addEventListener(
      "change",
      (e) => {
        if (e.target.type !== "radio") return;

        // 1. Update label text directly (e.g. "Color: Red" → "Color: Space Grey")
        this.updatePickerOptionLabel(picker, e.target);

        // 2. Ensure product variants are loaded for availability computation
        if (!this._productVariants) {
          const script = this._find("[data-product-variants]");
          if (script) {
            try {
              this._productVariants = JSON.parse(script.textContent);
            } catch (_) {}
          }
        }

        // 3. Update linked-option availability immediately (may auto-switch
        //    checked radios when the current selection becomes unavailable).
        //    This must run BEFORE resolveVariantFromPicker so the variant
        //    resolution sees the final selection including any auto-switches.
        if (this._productVariants) {
          this.updateBundleOptionAvailability(picker, this._productVariants);
        }

        // 4. Determine variant from final selections and update image/price/state
        this.resolveVariantFromPicker(picker);

        // 4. Forward ALL checked inner radios to outer picker (includes
        //    both the user's click and any auto-switched radios).
        let lastChangedOuter = null;
        picker
          .querySelectorAll('input[type="radio"]:checked')
          .forEach((innerRadio) => {
            const outerName = innerRadio.name.replace("-featured_product", "");
            let outerInput = null;
            if (formId) {
              outerInput = document.querySelector(
                `input[type="radio"][form="${CSS.escape(formId)}"][name="${CSS.escape(outerName)}"][value="${CSS.escape(innerRadio.value)}"]`,
              );
              if (outerInput && picker.contains(outerInput)) outerInput = null;
            }
            if (!outerInput) {
              outerInput = form.querySelector(
                `input[name="${CSS.escape(outerName)}"][value="${CSS.escape(innerRadio.value)}"]`,
              );
            }
            if (outerInput && !outerInput.checked) {
              outerInput.checked = true;
              lastChangedOuter = outerInput;
            }
          });

        // Dispatch a single change event on the last changed outer radio
        // to trigger the outer VariantPicker's selectCombination once.
        if (lastChangedOuter) {
          lastChangedOuter.dispatchEvent(
            new Event("change", { bubbles: true }),
          );
        }

        // Sync addon card variant selects to match the picked option value
        this.syncAddonVariants();
      },
      { signal },
    );
  }

  /**
   * Directly update the option value label text when a radio is selected.
   * e.g. "Color: Red" → "Color: Space Grey"
   */
  updatePickerOptionLabel(picker, radio) {
    const fieldset = radio.closest("fieldset.variant-picker__option");
    if (!fieldset) return;

    const label = radio.id
      ? picker.querySelector(`label[for="${CSS.escape(radio.id)}"]`)
      : null;
    const labelText = label
      ? (
          label.querySelector(".sr-only")?.textContent || label.textContent
        ).trim()
      : "";
    if (!labelText) return;

    const valueSpan = fieldset.querySelector(
      ".variant-picker__option-info .h-stack > legend + span",
    );
    if (valueSpan) {
      valueSpan.textContent = labelText;
    }
  }

  /**
   * Determine the current variant from the inner picker's selected options
   * using the embedded product variants JSON, then call onVariantSwitch.
   */
  resolveVariantFromPicker(picker) {
    if (!this._productVariants) {
      const script = this._find("[data-product-variants]");
      if (!script) return;
      try {
        this._productVariants = JSON.parse(script.textContent);
      } catch (_) {
        return;
      }
    }

    // Collect selected option texts from each fieldset (in order)
    const selectedTexts = [];
    picker.querySelectorAll("fieldset.variant-picker__option").forEach((fs) => {
      const checked = fs.querySelector('input[type="radio"]:checked');
      if (!checked) return;
      const label = checked.id
        ? picker.querySelector(`label[for="${CSS.escape(checked.id)}"]`)
        : null;
      const text = label
        ? (
            label.querySelector(".sr-only")?.textContent || label.textContent
          ).trim()
        : "";
      selectedTexts.push(text);
    });

    if (selectedTexts.length === 0) return;

    // Match against product variants by options array
    const variant = this._productVariants.find((v) =>
      v.options.every((opt, idx) => opt === selectedTexts[idx]),
    );

    if (variant) {
      this.onVariantSwitch(variant);
    } else {
      // No matching variant — show Unavailable
      this.updateBuyButtonState(null);
    }
  }

  interceptRerender(form) {
    const signal = this._abortController.signal;

    form.addEventListener(
      "product:rerender",
      (e) => {
        const modal = this._find("[data-configurator-modal]");
        const modalOpen = modal && !modal.hidden;

        // When modal is closed, don't block theme rerender but still
        // update outer buy button state from the rerendered variant.
        // Use requestAnimationFrame so it runs AFTER the theme's handler
        // has replaced the DOM elements.
        if (!modalOpen) {
          const fragment = e.detail.htmlFragment;
          const newPicker =
            fragment?.querySelector?.("variant-picker") ||
            fragment
              ?.querySelector?.("[data-variant]")
              ?.closest("variant-picker");
          if (newPicker) {
            const variantJson = newPicker.querySelector("script[data-variant]");
            try {
              const variant = variantJson
                ? JSON.parse(variantJson.textContent)
                : null;
              // Double rAF ensures we run after theme's DOM replacement
              requestAnimationFrame(() =>
                requestAnimationFrame(() => this.updateBuyButtonState(variant)),
              );
            } catch (_) {}
          }
          return;
        }

        e.stopImmediatePropagation();
        e.stopPropagation();

        const fragment = e.detail.htmlFragment;
        const newPicker =
          fragment?.querySelector?.("variant-picker") ||
          fragment
            ?.querySelector?.("[data-variant]")
            ?.closest("variant-picker");
        if (!newPicker) return;

        const variantJson = newPicker.querySelector("script[data-variant]");

        try {
          const variant = variantJson
            ? JSON.parse(variantJson.textContent)
            : null;
          if (variant) {
            this.onVariantSwitch(variant);
          } else {
            // No matching variant exists — show Unavailable
            this.updateBuyButtonState(null);
          }
        } catch (_) {}

        // Replace inner picker fieldsets with the new ones from the fragment,
        // giving identical behavior to the page's variant-picker.
        const innerPicker = this._innerPicker;
        if (innerPicker && newPicker) {
          const innerFieldsets = innerPicker.querySelectorAll("fieldset");
          const newFieldsets = newPicker.querySelectorAll("fieldset");

          newFieldsets.forEach((newFieldset, index) => {
            if (innerFieldsets[index]) {
              const clone = newFieldset.cloneNode(true);
              // Strip form attrs BEFORE inserting to prevent browser
              // radio-group deduplication from unchecking outer radios.
              clone
                .querySelectorAll('input[type="radio"][form]')
                .forEach((r) => r.removeAttribute("form"));
              innerFieldsets[index].replaceWith(clone);
            }
          });

          // Fix duplicate IDs introduced by cloning rerendered fieldsets
          this.deduplicatePickerIds(innerPicker);

          // Ensure picker-level form-id stays removed
          innerPicker.removeAttribute("form-id");

          // Update variant JSON script
          const existingScript = innerPicker.querySelector(
            "script[data-variant]",
          );
          if (existingScript && variantJson) {
            existingScript.textContent = variantJson.textContent;
          }
        }

        // Defer outer picker fieldset update so inner picker renders first
        requestAnimationFrame(() => this.updateOuterPickerFieldsets(fragment));
      },
      { capture: true, signal },
    );
  }

  /**
   * Update the outer variant picker's fieldsets from the rerender HTML fragment.
   * This keeps is-disabled/availability classes in sync without replacing the
   * entire outer picker (which would break event listeners).
   */
  updateOuterPickerFieldsets(htmlFragment) {
    const form = this._outerForm;
    if (!form) return;

    // Find the outer variant-picker (the one NOT inside the configurator modal)
    const outerPicker = form
      .closest("section, [data-section-id]")
      ?.querySelector("variant-picker:not([context='featured_product'])");
    if (!outerPicker) return;

    // Find the matching outer picker in the new HTML
    const newOuterPicker =
      htmlFragment?.querySelector?.(
        "variant-picker:not([context='featured_product'])",
      ) ||
      htmlFragment?.querySelector?.("variant-picker[context='main_product']");
    if (!newOuterPicker) return;

    const outerFieldsets = outerPicker.querySelectorAll("fieldset");
    const newFieldsets = newOuterPicker.querySelectorAll("fieldset");

    newFieldsets.forEach((newFieldset, index) => {
      if (outerFieldsets[index]) {
        outerFieldsets[index].replaceWith(newFieldset.cloneNode(true));
      }
    });

    // Update the outer picker's variant JSON script too
    const outerScript = outerPicker.querySelector("script[data-variant]");
    const newScript = newOuterPicker.querySelector("script[data-variant]");
    if (outerScript && newScript) {
      outerScript.textContent = newScript.textContent;
    }
  }

  syncOuterToInner(picker, form) {
    const signal = this._abortController.signal;
    const formId = form.getAttribute("id");

    form.addEventListener(
      "variant:change",
      (e) => {
        if (!e.detail?.variant) return;

        this.onVariantSwitch(e.detail.variant);

        // Sync inner picker radios to match outer checked state.
        // Outer radios use form="..." attribute — search document, not just form.
        picker.querySelectorAll('input[type="radio"]').forEach((innerInput) => {
          const outerName = innerInput.name.replace("-featured_product", "");
          let outerInput = null;
          if (formId) {
            outerInput = document.querySelector(
              `input[type="radio"][form="${CSS.escape(formId)}"][name="${CSS.escape(outerName)}"][value="${CSS.escape(innerInput.value)}"]`,
            );
            if (outerInput && picker.contains(outerInput)) outerInput = null;
          }
          if (!outerInput) {
            outerInput = form.querySelector(
              `input[name="${CSS.escape(outerName)}"][value="${CSS.escape(innerInput.value)}"]`,
            );
          }

          if (outerInput && outerInput.checked && !innerInput.checked) {
            innerInput.checked = true;
            // Update inner picker label to match
            this.updatePickerOptionLabel(picker, innerInput);
          }
        });
      },
      { signal },
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Variant Switch
  // Handles variant change events: price, button, discount, addon sync.
  // ═══════════════════════════════════════════════════════════════════════════

  onVariantSwitch(variant) {
    if (!variant) return;

    const optionRadio = this._find(
      '.cfg-option--selected input[name="configurator-option"]',
    );
    this.state.basePrice = this.getPromoPrice(variant.price || 0, optionRadio);
    this.updateConfiguratorOptionPrice(variant, optionRadio);
    this.updateFreeItems(variant.id);
    this.updateAddonItems(variant.id);

    this.updateBuyButtonState(variant);

    const mainIdInput = this._find("[data-configurator-main-id]");
    if (mainIdInput) mainIdInput.value = variant.id;

    if (optionRadio) {
      optionRadio.value = variant.id;
      optionRadio.dataset.price = variant.price;
      optionRadio.dataset.compareAtPrice = variant.compare_at_price || 0;
    }
    this.updatePrices();

    const selectedOption = this._find(".cfg-option--selected");

    const cardFigure = selectedOption?.querySelector(
      ".horizontal-product-card__figure",
    );
    if (cardFigure && variant.featured_image?.src) {
      const img = cardFigure.querySelector("img");
      if (img) {
        img.src = variant.featured_image.src;
        img.srcset = "";
      }
    }

    // Defer addon sync to next frame so picker/button updates render first
    requestAnimationFrame(() => this.syncAddonVariants());
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Addon & Free Item Updates
  // Match addon selects to main product option values, sync images, free items.
  // ═══════════════════════════════════════════════════════════════════════════

  syncAddonVariants() {
    const picker = this._activeBundlePicker || this._innerPicker;
    if (!picker) return;

    // Collect currently selected option TEXT values from the variant picker
    // (radio.value is a numeric ID; the display text is in the associated label)
    const selectedValues = [];
    picker.querySelectorAll('input[type="radio"]:checked').forEach((radio) => {
      if (!radio.id) return;
      const label = picker.querySelector(
        `label[for="${CSS.escape(radio.id)}"]`,
      );
      const text = label ? label.textContent.trim() : "";
      if (text) selectedValues.push(text);
    });

    if (selectedValues.length === 0) return;

    const selects = this._findAll(".cfg-product .cfg-variant-select select");
    selects.forEach((select) => {
      const card = select.closest(".addon-product-block");
      const checkbox = card?.querySelector(".wo-checkbox[data-category]");
      const isBundleIncluded = checkbox?.dataset.bundleIncluded === "true";

      let matched = false;
      for (const option of select.options) {
        if (matched) break;
        if (!option.value) continue;
        // Skip disabled options for non-bundle cards (user can't select sold-out)
        if (option.disabled && !isBundleIncluded) continue;
        const text = option.textContent.trim();
        for (const val of selectedValues) {
          if (text === val || text.includes(val) || val.includes(text)) {
            if (option.disabled && isBundleIncluded) {
              // Bundle card: can't change select value for disabled option,
              // but sync the image directly from option's data-image
              const img = card?.querySelector(
                ".horizontal-product-card__image",
              );
              if (img && option.dataset.image) {
                img.src = option.dataset.image;
                img.srcset = "";
              }
            } else if (option.value !== select.value) {
              const oldValue = select.value;
              select.value = option.value;

              // Swap IDs in bundleIncludedIds for bundle cards
              if (isBundleIncluded) {
                this.state.bundleIncludedIds.delete(oldValue);
                this.state.bundleIncludedIds.add(option.value);
              }

              this.onAddonVariantChange(select);
            }
            matched = true;
            break;
          }
        }
      }
    });

    // Sync bundle-included cards that have no <select> — use variant-images JSON
    this._findAll(".addon-product-block.is-bundle-included").forEach((card) => {
      const script = card.querySelector("script[data-variant-images]");
      if (!script) return;
      let variants;
      try {
        variants = JSON.parse(script.textContent);
      } catch (_) {
        return;
      }
      const img = card.querySelector(".horizontal-product-card__image");
      if (!img) return;

      for (const v of variants) {
        const title = v.title || "";
        const matched = selectedValues.some(
          (val) => title === val || title.includes(val) || val.includes(title),
        );
        if (matched && v.image) {
          const currentFile = img.src.split("/").pop()?.split("?")[0] || "";
          const targetFile = v.image.split("/").pop()?.split("?")[0] || "";
          if (currentFile !== targetFile) {
            img.src = v.image;
            img.srcset = "";
          }

          // Also update the checkbox variant ID so the correct variant is added to cart
          const checkbox = card.querySelector(
            '.wo-checkbox[data-bundle-included="true"]',
          );
          if (checkbox && v.id) {
            const oldId = checkbox.value;
            const newId = String(v.id);
            if (oldId !== newId) {
              checkbox.name = `addon-${newId}`;
              checkbox.value = newId;

              // Update bundleIncludedIds
              this.state.bundleIncludedIds.delete(oldId);
              this.state.bundleIncludedIds.add(newId);

              // Update selectedAddons so badge counts stay correct
              const section = card.closest("[data-category-section]");
              const categoryId = section?.dataset.categorySection;
              if (categoryId) {
                const catSet = this.state.selectedAddons.get(categoryId);
                if (catSet) {
                  catSet.delete(oldId);
                  catSet.add(newId);
                }
              }
            }
          }
          break;
        }
      }
    });

    // After variant matching, sync images for all addon cards
    // (handles server-render mismatch where image shows sold-out variant
    //  but select already defaults to first available)
    this.syncAddonCardImages();
  }

  /**
   * Force-sync all addon card images to match their select's currently
   * selected variant. Fixes mismatch when server renders a sold-out
   * variant's image but the <select> defaults to first available.
   */
  syncAddonCardImages() {
    this._findAll(".cfg-product .cfg-variant-select select").forEach(
      (select) => {
        const selectedOption = select.options[select.selectedIndex];
        if (!selectedOption || !selectedOption.value) return;
        const imageUrl = selectedOption.dataset.image;
        if (!imageUrl) return;

        const card = select.closest(".addon-product-block");
        if (!card) return;
        const img = card.querySelector(".horizontal-product-card__image");
        if (!img) return;

        // Only update if the current image doesn't match
        const currentFile = img.src.split("/").pop()?.split("?")[0] || "";
        const targetFile = imageUrl.split("/").pop()?.split("?")[0] || "";
        if (currentFile !== targetFile) {
          img.src = imageUrl;
          img.srcset = "";
        }
      },
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Metafield-driven Content Updates
  // Update free items and addon items HTML from server-rendered Liquid.
  // ═══════════════════════════════════════════════════════════════════════════

  updateFreeItems(variantId) {
    const container = this._find("[data-configurator-free-items]");
    if (!container) return;

    if (!this._freeItemsMap) {
      const script = document.querySelector(
        "#variant_metafield_configurator-free-items",
      );
      if (!script) return;
      try {
        this._freeItemsMap = JSON.parse(script.textContent.replace(/\n/g, ""));
      } catch (_) {
        return;
      }
    }

    const html = this._freeItemsMap[String(variantId)];
    if (html !== undefined) {
      // Content is pre-rendered server-side Liquid (trusted)
      container.innerHTML = html;
    }
  }

  updateAddonItems(variantId) {
    const container = this._find("[data-configurator-addon-items]");
    if (!container) return;

    if (!this._addonItemsMap) {
      const script = document.querySelector(
        "#variant_metafield_configurator-addon-items",
      );
      if (!script) return;
      try {
        this._addonItemsMap = JSON.parse(script.textContent.replace(/\n/g, ""));
      } catch (_) {
        return;
      }
    }

    const html = this._addonItemsMap[String(variantId)];
    if (html !== undefined) {
      // Content is pre-rendered server-side Liquid (trusted)
      container.innerHTML = html;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Price Calculation & Display
  // Discount price visibility, bundle vs single pricing toggle.
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Parse per-option discount map string "|addonId:price|addonId:price|" into { addonId: price }.
   * @param {string} mapStr
   * @returns {Record<string, number>}
   */
  _parseDiscountMapString(mapStr) {
    const out = {};
    if (!mapStr || typeof mapStr !== "string") return out;
    const parts = mapStr.split("|").filter(Boolean);
    for (const part of parts) {
      const idx = part.indexOf(":");
      if (idx > 0) {
        const addonId = part.slice(0, idx);
        const price = parseInt(part.slice(idx + 1), 10);
        if (!isNaN(price)) out[addonId] = price;
      }
    }
    return out;
  }

  /**
   * Toggle addon pricing by selected option: only show discounted price when the
   * selected option (main or specific bundle) has that addon in its discount map.
   * Reads the currently checked option from the DOM.
   * @param {string|null} [_selectedOptionProductId] – optional; we always read from DOM
   */
  updateDiscountPrices(_selectedOptionProductId) {
    const container = this._find("[data-configurator-products]");
    if (!container) return;

    const checkedRadio = this._find(
      'input[name="configurator-option"]:checked',
    );
    const selectedOptionProductId =
      checkedRadio?.dataset.optionProductId != null
        ? String(checkedRadio.dataset.optionProductId)
        : null;

    const raw = container.getAttribute("data-configurator-discounts-by-option");
    let discountsByOption = {};
    if (raw) {
      try {
        discountsByOption = JSON.parse(raw);
      } catch (_) {
        // leave empty
      }
    }

    const optionKey =
      selectedOptionProductId != null ? String(selectedOptionProductId) : null;
    const mapStr =
      optionKey != null ? (discountsByOption[optionKey] ?? null) : null;
    const optionDiscountMap =
      typeof mapStr === "string" ? this._parseDiscountMapString(mapStr) : {};

    this._findAll(".addon-product-block").forEach((card) => {
      if (card.querySelector('input[name="configurator-option"]')) return;
      if (!card.dataset.addonProductId) return;

      const isBundleIncluded = card.classList.contains("is-bundle-included");

      const addonProductId = card.dataset.addonProductId;
      let compareAt = card.querySelector("compare-at-price");
      let salePrice = card.querySelector("sale-price");
      const checkbox = card.querySelector(".wo-checkbox[data-category]");

      const discountedPrice =
        addonProductId != null ? optionDiscountMap[addonProductId] : undefined;
      const showDiscounts =
        discountedPrice !== undefined &&
        discountedPrice !== "" &&
        !isNaN(parseInt(discountedPrice, 10));

      // Skip non-free bundle-included cards (their price is already rendered by Liquid)
      if (
        isBundleIncluded &&
        !(showDiscounts && parseInt(discountedPrice, 10) === 0)
      )
        return;

      if (showDiscounts) {
        const priceInt = parseInt(discountedPrice, 10) || 0;
        const regularPrice =
          checkbox?.dataset.regularPrice || checkbox?.dataset.price || "0";
        const regularPriceInt = parseInt(regularPrice, 10) || 0;

        // FREE gift: show "FREE" badge with strikethrough original price + auto-check
        if (priceInt === 0 && regularPriceInt > 0) {
          const priceList =
            salePrice?.closest(".price-list") ||
            card.querySelector(".price-list");
          if (priceList) {
            priceList.dataset.originalHtml =
              priceList.dataset.originalHtml || priceList.innerHTML;
            priceList.classList.add("cfg-bundle-price");
            const freeSpan = document.createElement("span");
            freeSpan.className = "cfg-bundle-free";
            freeSpan.textContent = "FREE";
            const priceSpan = document.createElement("span");
            priceSpan.className = "text-subdued line-through";
            priceSpan.textContent = this.formatMoney(regularPriceInt, false);
            priceList.replaceChildren(freeSpan, priceSpan);
          }

          // Auto-check and lock free gift items (skip already-locked bundle items)
          if (checkbox && !checkbox.dataset.bundleIncluded) {
            checkbox.checked = true;
            checkbox.disabled = true;
            checkbox.dataset.freeIncluded = "true";
            card.classList.add("is-selected");

            const variantId = String(checkbox.value);
            if (!this.state.freeIncludedIds) {
              this.state.freeIncludedIds = new Set();
            }
            this.state.freeIncludedIds.add(variantId);

            const section = checkbox.closest("[data-category-section]");
            const categoryId = section?.dataset.categorySection;
            if (categoryId) {
              if (!this.state.selectedAddons.has(categoryId)) {
                this.state.selectedAddons.set(categoryId, new Set());
              }
              this.state.selectedAddons.get(categoryId).add(variantId);
            }
          }
        } else if (priceInt !== regularPriceInt) {
          if (salePrice) {
            salePrice.hidden = false;
            salePrice.classList.add("text-on-sale");
            const srSpan = salePrice.querySelector(".sr-only");
            salePrice.textContent = this.formatMoney(priceInt);
            if (srSpan) salePrice.prepend(srSpan);
          }
          if (!compareAt && salePrice?.parentElement) {
            compareAt = document.createElement("compare-at-price");
            compareAt.className = "text-subdued line-through";
            const srSpan = document.createElement("span");
            srSpan.className = "sr-only";
            srSpan.textContent = "Regular price";
            compareAt.appendChild(srSpan);
            compareAt.appendChild(
              document.createTextNode(this.formatMoney(regularPriceInt, false)),
            );
            salePrice.parentElement.insertBefore(compareAt, salePrice);
          }
          if (compareAt) {
            compareAt.hidden = false;
            compareAt.classList.add("text-subdued", "line-through");
          }
        }
        if (checkbox) {
          checkbox.dataset.regularPrice =
            checkbox.dataset.regularPrice || checkbox.dataset.price;
          checkbox.dataset.price = String(priceInt);
          checkbox.dataset.discountedPrice = String(priceInt);
        }
      } else {
        // Restore FREE gift items back to normal state
        const priceList = card.querySelector(".price-list.cfg-bundle-price");
        if (priceList?.dataset.originalHtml) {
          priceList.innerHTML = priceList.dataset.originalHtml;
          priceList.classList.remove("cfg-bundle-price");
          delete priceList.dataset.originalHtml;
          compareAt = priceList.querySelector("compare-at-price");
          salePrice = priceList.querySelector("sale-price");
        }

        // Unlock free gift checkbox
        if (checkbox?.dataset.freeIncluded === "true") {
          checkbox.checked = false;
          checkbox.disabled = false;
          delete checkbox.dataset.freeIncluded;
          card.classList.remove("is-selected");

          const variantId = String(checkbox.value);
          this.state.freeIncludedIds?.delete(variantId);

          const section = checkbox.closest("[data-category-section]");
          const categoryId = section?.dataset.categorySection;
          if (categoryId && this.state.selectedAddons.has(categoryId)) {
            this.state.selectedAddons.get(categoryId).delete(variantId);
          }
        }

        const regularPrice =
          checkbox?.dataset.regularPrice || checkbox?.dataset.price || "0";
        if (salePrice) {
          salePrice.classList.remove("text-on-sale");
          salePrice.hidden = !!compareAt;
          const saleSrSpan = salePrice.querySelector(".sr-only");
          salePrice.textContent = this.formatMoney(
            parseInt(regularPrice, 10) || 0,
          );
          if (saleSrSpan) salePrice.prepend(saleSrSpan);
        }
        if (compareAt) {
          compareAt.hidden = false;
          compareAt.classList.remove("text-subdued", "line-through");
          const regularFormatted = this.formatMoney(
            parseInt(regularPrice, 10) || 0,
            false,
          );
          const compareSrSpan = compareAt.querySelector(".sr-only");
          compareAt.textContent = regularFormatted;
          if (compareSrSpan) compareAt.prepend(compareSrSpan);
        } else if (salePrice) {
          salePrice.hidden = false;
          const srSpan = salePrice.querySelector(".sr-only");
          salePrice.textContent = this.formatMoney(
            parseInt(regularPrice, 10) || 0,
          );
          if (srSpan) salePrice.prepend(srSpan);
        }
        if (checkbox) {
          if (checkbox.dataset.regularPrice) {
            checkbox.dataset.price = checkbox.dataset.regularPrice;
          }
          delete checkbox.dataset.discountedPrice;
        }
      }
    });
    this.updatePrices();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Bundle Inclusions
  // Load, apply, and clear bundle-included product states.
  // ═══════════════════════════════════════════════════════════════════════════

  loadBundleIncludedIds() {
    const script = this._find("[data-bundle-included-ids]");
    if (!script) return;

    try {
      const ids = JSON.parse(script.textContent);
      if (Array.isArray(ids)) {
        ids.forEach((id) => {
          if (id) this.state.bundleIncludedIds.add(String(id));
        });
      }
    } catch (_) {
      // Silent fail
    }
  }

  registerFreeItems() {
    this.state.freeIncludedIds = new Set();
    this._findAll('.wo-checkbox[data-free-included="true"]').forEach(
      (input) => {
        const variantId = String(input.value);
        this.state.freeIncludedIds.add(variantId);

        const section = input.closest("[data-category-section]");
        const categoryId = section?.dataset.categorySection;
        if (categoryId) {
          if (!this.state.selectedAddons.has(categoryId)) {
            this.state.selectedAddons.set(categoryId, new Set());
          }
          this.state.selectedAddons.get(categoryId).add(variantId);
        }
      },
    );
  }

  applyBundleInclusions() {
    if (this.state.bundleIncludedIds.size === 0) return;

    this._findAll(".wo-checkbox[data-category]").forEach((input) => {
      const card = input.closest(".addon-product-block");

      // First check if any select option matches a bundle variant ID.
      // The user may have changed the select while in single-product mode,
      // so the current checkbox value might not match the bundle variant.
      const select = card?.querySelector(".cfg-variant-select select");
      let matchedVariantId = null;
      if (select) {
        for (const option of select.options) {
          if (
            option.value &&
            this.state.bundleIncludedIds.has(String(option.value))
          ) {
            matchedVariantId = String(option.value);
            break;
          }
        }
        // Force-select the bundle variant if found and different
        if (matchedVariantId && select.value !== matchedVariantId) {
          select.value = matchedVariantId;
          this.onAddonVariantChange(select);
        }
      }

      const variantId = String(input.value);
      if (!this.state.bundleIncludedIds.has(variantId) && !matchedVariantId)
        return;

      // Capture free-included state BEFORE it may be deleted below.
      // Free items (e.g. GuideStar 61) stay user-selectable even when they
      // also appear in bundleIncludedIds.
      const isFreeItem = input.dataset.freeIncluded === "true";

      input.checked = true;
      input.disabled = true;
      input.dataset.bundleIncluded = "true";

      // Clear free-gift state if previously set by discount system
      if (input.dataset.freeIncluded === "true") {
        delete input.dataset.freeIncluded;
        card?.classList.remove("is-selected");
      }

      if (card) {
        card.classList.add("is-bundle-included");

        const hCard = card.querySelector(".horizontal-product-card");
        if (hCard) hCard.classList.add("horizontal-product-card--bundle");

        // Restore original price if it was replaced by FREE from discount
        const priceList = card.querySelector(".price-list");
        if (
          priceList?.dataset.originalHtml &&
          priceList.querySelector(".cfg-bundle-free")
        ) {
          priceList.innerHTML = priceList.dataset.originalHtml;
          priceList.classList.remove("cfg-bundle-price");
        }
        if (priceList && !priceList.classList.contains("cfg-bundle-price")) {
          priceList.dataset.originalHtml = priceList.innerHTML;
          priceList.classList.add("cfg-bundle-price");
        }

        if (!card.querySelector(".cfg-double-check")) {
          input.hidden = true;
          const svg = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg",
          );
          svg.classList.add("cfg-double-check");
          svg.setAttribute("width", "24");
          svg.setAttribute("height", "24");
          svg.setAttribute("viewBox", "0 0 24 24");
          svg.setAttribute("fill", "none");
          // SVG paths are static trusted content
          svg.innerHTML =
            '<path d="M18 6L7 17L2 12" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>' +
            '<path d="M22 10L14.5 17.5L13 16" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>';
          input.parentElement.appendChild(svg);
        }

        // Don't lock the select for free items — user should still be able to pick variants
        if (!isFreeItem) {
          const selectWrapper = card.querySelector(".cfg-variant-select");
          if (selectWrapper) {
            selectWrapper.classList.add("cfg-variant-select--locked");
            // Disable the <select> element directly — iOS Safari ignores
            // pointer-events:none on native <select> and still opens the picker
            const sel = selectWrapper.querySelector("select");
            if (sel) sel.disabled = true;
          }
        }
      }

      const section = input.closest("[data-category-section]");
      const categoryId = section?.dataset.categorySection;
      if (categoryId) {
        if (!this.state.selectedAddons.has(categoryId)) {
          this.state.selectedAddons.set(categoryId, new Set());
        }
        this.state.selectedAddons.get(categoryId).add(variantId);
      }
    });

    this.updateCategoryBadges();
  }

  clearBundleInclusions() {
    this.state.bundleIncludedIds.clear();

    // Unlock all variant selects that were locked for bundle
    this._findAll(".cfg-variant-select--locked").forEach((el) => {
      el.classList.remove("cfg-variant-select--locked");
      const sel = el.querySelector("select");
      if (sel) sel.disabled = false;
    });

    this._findAll('.wo-checkbox[data-bundle-included="true"]').forEach(
      (input) => {
        // Skip permanently free items — they stay FREE regardless of bundle/single mode
        if (input.dataset.freeIncluded === "true") return;

        input.checked = false;
        input.disabled = false;
        delete input.dataset.bundleIncluded;

        const card = input.closest(".addon-product-block");
        if (card) {
          card.classList.remove("is-bundle-included");

          const hCard = card.querySelector(".horizontal-product-card");
          if (hCard) hCard.classList.remove("horizontal-product-card--bundle");

          const priceList = card.querySelector(".price-list.cfg-bundle-price");
          if (priceList) {
            priceList.classList.remove("cfg-bundle-price");
            if (priceList.dataset.originalHtml) {
              // Restoring previously saved server-rendered HTML (trusted)
              priceList.innerHTML = priceList.dataset.originalHtml;
            } else {
              const price = parseInt(input.dataset.price, 10) || 0;
              // Price HTML is built from trusted variant data attributes
              priceList.innerHTML = `<sale-price class="text-subdued"><span class="sr-only">Sale price</span>${this.formatMoney(price)}</sale-price>`;
            }
          }

          const svg = card.querySelector(".cfg-double-check");
          if (svg) svg.remove();
          input.hidden = false;
        }

        const section = input.closest("[data-category-section]");
        const categoryId = section?.dataset.categorySection;
        if (categoryId && this.state.selectedAddons.has(categoryId)) {
          this.state.selectedAddons.get(categoryId).delete(input.value);
        }

        const productEl = input.closest(".cfg-product");
        if (productEl) productEl.classList.remove("is-selected");
      },
    );

    this.updateCategoryBadges();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Event Handlers & Delegation
  // Delegated click/change handlers, card interactions.
  // ═══════════════════════════════════════════════════════════════════════════

  bindEvents() {
    const signal = this._abortController.signal;

    // Delegated click handler for all interactive elements.
    // Bound to document so it fires even when the modal is appended to <body>.
    document.addEventListener(
      "click",
      (e) => {
        // Handle [data-configurator-open] first (including sticky bar button outside this)
        const openBtn = e.target.closest?.("[data-configurator-open]");
        if (
          openBtn &&
          (this.contains(openBtn) ||
            openBtn.getAttribute("aria-controls") === this._modal?.id)
        ) {
          e.preventDefault();
          e.stopPropagation();
          this.openModal();
          return;
        }

        // Early return: skip if click is outside this configurator and its modal
        if (
          !this.contains(e.target) &&
          !(this._modal && this._modal.contains(e.target))
        )
          return;

        const closeBtn = e.target.closest?.("[data-configurator-close]");
        if (
          closeBtn &&
          (this.contains(closeBtn) || this._modal?.contains(closeBtn))
        ) {
          e.preventDefault();
          e.stopPropagation();
          this.closeModal();
          return;
        }

        const tabBtn = e.target.closest?.(".cfg-tabs [data-tab]");
        if (
          tabBtn &&
          (this.contains(tabBtn) || this._modal?.contains(tabBtn))
        ) {
          e.preventDefault();
          e.stopPropagation();
          this.switchTab(tabBtn.dataset.tab);
          return;
        }

        const categoryBtn = e.target.closest?.("[data-category-handle]");
        if (
          categoryBtn &&
          (this.contains(categoryBtn) || this._modal?.contains(categoryBtn))
        ) {
          e.preventDefault();
          e.stopPropagation();
          this.selectCategory(categoryBtn.dataset.categoryHandle);
          return;
        }

        const previewBtn = e.target.closest?.("[data-cfg-preview]");
        if (
          previewBtn &&
          (this.contains(previewBtn) || this._modal?.contains(previewBtn))
        ) {
          e.preventDefault();
          e.stopPropagation();
          const modalId = previewBtn.getAttribute("aria-controls");
          if (modalId) {
            const modal = document.getElementById(modalId);
            if (modal && typeof modal.show === "function") modal.show();
          }
          return;
        }
      },
      { capture: true, signal },
    );

    // ── Addon product cards ──────────────────────────────────────────────
    // All card interactions are handled per-card to keep logic co-located.
    // The <label class="cfg-product"> wraps a hidden <input type="checkbox">,
    // so we must preventDefault on the label to stop the browser from
    // scrolling to / focusing the input (which shifts the scroll container).
    const scrollContainer = this._find(".cfg-modal__container");

    this._findAll(".cfg-product").forEach((card) => {
      const input = card.querySelector(".wo-checkbox[data-category]");
      const select = card.querySelector(".cfg-variant-select select");
      if (!input) return;

      // Track scroll position before select receives focus (mousedown fires before focus)
      let scrollSnapshot = null;

      // — Card body click: toggle checkbox (+ open select when checking) —
      card.addEventListener(
        "click",
        (e) => {
          if (e.target.closest("select, input, [data-cfg-preview]")) return;
          if (input.disabled) return;

          e.preventDefault();
          input.checked = !input.checked;
          this.toggleProduct(input);

          // Open variant picker when checking a card that has one
          if (input.checked && select) {
            scrollSnapshot = scrollContainer?.scrollTop ?? null;
            requestAnimationFrame(() => {
              try {
                select.showPicker();
              } catch (_) {
                select.focus();
              }
            });
          }
        },
        { signal },
      );

      // — Direct checkbox click: normal toggle (no scroll issue) —
      input.addEventListener("change", () => this.toggleProduct(input), {
        signal,
      });

      if (!select) return;

      // — Select scroll-lock: save scrollTop on mousedown, restore on focus —
      select.addEventListener(
        "mousedown",
        () => {
          scrollSnapshot = scrollContainer?.scrollTop ?? null;
        },
        { signal },
      );
      select.addEventListener(
        "focus",
        () => {
          if (scrollSnapshot !== null && scrollContainer) {
            scrollContainer.scrollTop = scrollSnapshot;
            scrollSnapshot = null;
          }
        },
        { signal },
      );

      // — Variant change —
      select.addEventListener(
        "change",
        (e) => this.onAddonVariantChange(e.target),
        { signal },
      );
    });

    // ── Outer free-item card selects → sync to inner configurator cards ──
    this._findAll(
      "[data-configurator-free-items] [data-addon-product-id]",
    ).forEach((outerCard) => {
      const outerSelect = outerCard.querySelector("select");
      if (!outerSelect) return;

      outerSelect.addEventListener(
        "change",
        (e) => {
          const productId = outerCard.dataset.addonProductId;
          if (!productId) return;

          const selectedOption = e.target.options[e.target.selectedIndex];
          if (!selectedOption || !selectedOption.value) return;

          // Update outer card image
          if (selectedOption.dataset.image) {
            const img = outerCard.querySelector(
              ".horizontal-product-card__image",
            );
            if (img) {
              img.src = selectedOption.dataset.image;
              img.srcset = "";
            }
          }

          // Update outer hidden input for form submission
          const hiddenInput = outerCard.querySelector('input[type="hidden"]');
          if (hiddenInput) {
            hiddenInput.value = selectedOption.value;
          }

          // Sync to inner configurator card
          const innerCard = this._find(
            `.cfg-product[data-addon-product-id="${productId}"]`,
          );
          if (!innerCard) return;

          const innerSelect = innerCard.querySelector(
            ".cfg-variant-select select",
          );
          if (innerSelect && innerSelect.value !== selectedOption.value) {
            innerSelect.value = selectedOption.value;
            this.onAddonVariantChange(innerSelect);
          }
        },
        { signal },
      );
    });

    this._findAll('input[name="configurator-option"]').forEach((radio) => {
      radio.addEventListener("change", (e) => this.onOptionChange(e.target), {
        signal,
      });
    });

    const submitWrapper = this._find("[data-configurator-submit-wrapper]");
    if (submitWrapper) {
      submitWrapper.addEventListener("click", () => this.submit(), { signal });
    }

    document.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Escape") {
          const modal = this._find("[data-configurator-modal]");
          if (modal && !modal.hidden) this.closeModal();
        }
      },
      { signal },
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Option & Product Selection
  // Radio option change handling, addon variant change, product toggling.
  // ═══════════════════════════════════════════════════════════════════════════

  onOptionChange(radio) {
    this._findAll(".cfg-option").forEach((opt) => {
      opt.classList.toggle("cfg-option--selected", opt.contains(radio));
    });

    const optionPrice = parseInt(radio.dataset.price, 10) || 0;
    this.state.basePrice = this.getPromoPrice(optionPrice, radio);

    const mainIdInput = this._find("[data-configurator-main-id]");
    if (mainIdInput) mainIdInput.value = radio.value;

    this.clearBundleInclusions();

    const isBundle = radio.dataset.optionType === "bundle";

    if (isBundle) {
      this.activateBundleOption(radio);
    } else {
      // Restore main picker, hide all bundle pickers
      this._activeBundlePicker = null;
      const mainPickerWrapper = this._find("[data-main-variant-picker]");
      if (mainPickerWrapper) mainPickerWrapper.hidden = false;
      this._findAll("[data-bundle-picker-for]").forEach((w) => {
        w.hidden = true;
      });

      // Restore main product variants for resolution
      this._productVariants = null; // Force re-parse from main script on next resolve

      const mainPicker = this._find(
        "[data-main-variant-picker] variant-picker",
      );
      if (mainPicker) {
        const variant = this.autoSelectFirstAvailable(mainPicker);
        if (variant) this.onVariantSwitch(variant);
      }
    }

    const selectedOptionProductId =
      radio?.dataset.optionProductId != null
        ? String(radio.dataset.optionProductId)
        : null;
    this.toggleBundleOnlyCards(selectedOptionProductId, isBundle);
    this.updateDiscountPrices(selectedOptionProductId);
    this.updateCategoryBadges();
    this.updatePrices();
    this.saveSelectionState();
  }

  /**
   * Show or hide addon cards that are marked as bundle-only.
   * Cards with data-bundle-only-for are shown only when the selected option's
   * product id matches. Cards without it (legacy) are shown when any bundle is active.
   * @param {string|null} selectedOptionProductId – product id of the selected option
   * @param {boolean} isBundle – true when a bundle option is active
   */
  toggleBundleOnlyCards(selectedOptionProductId, isBundle) {
    this._findAll("[data-bundle-only]").forEach((card) => {
      const onlyFor = card.dataset.bundleOnlyFor;
      if (onlyFor != null && onlyFor !== "") {
        card.hidden = selectedOptionProductId !== onlyFor;
      } else {
        card.hidden = !isBundle;
      }
    });
  }

  onAddonVariantChange(select) {
    const card = select.closest(".addon-product-block");
    if (!card) return;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption || !selectedOption.value) return;

    const checkbox = card.querySelector(".wo-checkbox[data-category]");
    const oldVariantId = checkbox?.value;
    if (checkbox) {
      checkbox.value = selectedOption.value;
      const newPrice = selectedOption.dataset.price;
      if (newPrice) {
        // If currently showing discounted price (bundle mode or non-bundle page),
        // only update regularPrice; otherwise update data-price and regularPrice
        if (
          checkbox.dataset.discountedPrice &&
          checkbox.dataset.price === checkbox.dataset.discountedPrice
        ) {
          checkbox.dataset.regularPrice = newPrice;
        } else {
          checkbox.dataset.price = newPrice;
          if (checkbox.dataset.regularPrice) {
            checkbox.dataset.regularPrice = newPrice;
          }
        }
        const priceInt = parseInt(newPrice, 10) || 0;

        // Update regular price display
        const regularEl = card.querySelector(".cfg-price-regular sale-price");
        if (regularEl) {
          const srSpan = regularEl.querySelector(".sr-only");
          regularEl.textContent = this.formatMoney(priceInt);
          if (srSpan) regularEl.prepend(srSpan);
        }

        // Update discounted price display if visible
        const discountEl = card.querySelector(".cfg-price-discounted");
        if (discountEl && !discountEl.hidden) {
          const discountPrice =
            parseInt(discountEl.dataset.discountPrice, 10) || 0;
          if (discountPrice) {
            const saleClass =
              priceInt > discountPrice ? "text-on-sale" : "text-subdued";
            const compareClass = "text-subdued line-through";
            let html = `<price-list class="price-list"><sale-price class="${saleClass}"><span class="sr-only">Sale price</span>${this.formatMoney(discountPrice)}</sale-price>`;
            if (priceInt > discountPrice) {
              html += `<compare-at-price class="${compareClass}"><span class="sr-only">Regular price</span>${this.formatMoney(priceInt, false)}</compare-at-price>`;
            }
            html += `</price-list>`;
            discountEl.innerHTML = html;
          }
        }

        // Fallback: update old-style price-list (non-bundle cards without discount wrappers)
        if (!regularEl) {
          const priceEl = card.querySelector(
            ".price-list:not(.cfg-bundle-price) sale-price",
          );
          if (priceEl) {
            const srSpan = priceEl.querySelector(".sr-only");
            priceEl.textContent = this.formatMoney(priceInt);
            if (srSpan) priceEl.prepend(srSpan);
          }
        }
      }
    }

    const imageUrl = selectedOption.dataset.image;
    if (imageUrl) {
      const img = card.querySelector(".horizontal-product-card__image");
      if (img) {
        img.src = imageUrl;
        img.srcset = "";
      }
    }

    const section = checkbox?.closest("[data-category-section]");
    const categoryId = section?.dataset.categorySection;

    if (checkbox?.checked) {
      // Update the selectedAddons Set: swap old variant ID for new one
      if (categoryId) {
        const categorySelection = this.state.selectedAddons.get(categoryId);
        if (categorySelection) {
          categorySelection.delete(oldVariantId);
          categorySelection.add(selectedOption.value);
        }
      }

      // Keep freeIncludedIds in sync so badge stays gray (not red)
      if (
        checkbox.dataset.freeIncluded === "true" &&
        this.state.freeIncludedIds
      ) {
        this.state.freeIncludedIds.delete(oldVariantId);
        this.state.freeIncludedIds.add(selectedOption.value);
      }

      this.updateCategoryBadges();
      this.updatePrices();
    }

    // Sync variant to paired category
    if (categoryId) {
      const pairedId = this._categoryPairs[categoryId];
      if (pairedId) {
        const productId = card.dataset.addonProductId;
        const pairedSection = this._find(
          `[data-category-section="${pairedId}"]`,
        );
        const pairedCard = pairedSection?.querySelector(
          `[data-addon-product-id="${productId}"]`,
        );
        if (pairedCard) {
          const pairedSelect = pairedCard.querySelector(
            ".cfg-variant-select select",
          );
          const pairedCb = pairedCard.querySelector(
            ".wo-checkbox[data-category]",
          );
          if (pairedSelect && pairedSelect.value !== selectedOption.value) {
            const opt = pairedSelect.querySelector(
              `option[value="${selectedOption.value}"]`,
            );
            if (opt) pairedSelect.value = selectedOption.value;
          }
          if (pairedCb) {
            pairedCb.value = selectedOption.value;
            if (pairedCb.checked && this.state.selectedAddons.has(pairedId)) {
              const pairedSet = this.state.selectedAddons.get(pairedId);
              pairedSet.delete(oldVariantId);
              pairedSet.add(selectedOption.value);
            }
          }
        }
      }
    }

    // Sync inner card variant to outer free-item card
    const productId = card.dataset.addonProductId;
    if (productId) {
      const outerCard = this.querySelector(
        `[data-configurator-free-items] [data-addon-product-id="${productId}"]`,
      );
      if (outerCard) {
        const outerSelect = outerCard.querySelector("select");
        if (outerSelect && outerSelect.value !== selectedOption.value) {
          outerSelect.value = selectedOption.value;
        }
        const imageUrl = selectedOption.dataset.image;
        if (imageUrl) {
          const outerImg = outerCard.querySelector(
            ".horizontal-product-card__image",
          );
          if (outerImg) {
            outerImg.src = imageUrl;
            outerImg.srcset = "";
          }
        }
        const hiddenInput = outerCard.querySelector('input[type="hidden"]');
        if (hiddenInput) {
          hiddenInput.value = selectedOption.value;
        }
      }
    }
  }

  toggleProduct(input) {
    if (input.dataset.bundleIncluded === "true") {
      input.checked = true;
      return;
    }

    const card = input.closest(".addon-product-block");
    const select = card?.querySelector(".cfg-variant-select select");

    if (input.checked && select && !select.value) {
      const firstAvailable = select.querySelector(
        "option[value]:not([disabled])",
      );
      if (firstAvailable) {
        select.value = firstAvailable.value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    const section = input.closest("[data-category-section]");
    const categoryId = section?.dataset.categorySection;
    const variantId = input.value;

    if (!categoryId) return;

    if (!this.state.selectedAddons.has(categoryId)) {
      this.state.selectedAddons.set(categoryId, new Set());
    }

    const categorySelection = this.state.selectedAddons.get(categoryId);

    if (input.checked) {
      categorySelection.add(variantId);
    } else {
      categorySelection.delete(variantId);
    }

    const productEl = input.closest(".cfg-product");
    if (productEl) {
      productEl.classList.toggle("is-selected", input.checked);
    }

    this.syncPairedCategory(input, categoryId);
    this.updateCategoryBadges();
    this.updatePrices();
    this.saveSelectionState();
  }

  syncPairedCategory(input, categoryId) {
    const pairedId = this._categoryPairs[categoryId];
    if (!pairedId) return;

    const productId = input.closest("[data-addon-product-id]")?.dataset
      .addonProductId;
    if (!productId) return;

    const pairedSection = this._find(`[data-category-section="${pairedId}"]`);
    if (!pairedSection) return;

    const pairedCard = pairedSection.querySelector(
      `[data-addon-product-id="${productId}"]`,
    );
    if (!pairedCard) return;

    const pairedCheckbox = pairedCard.querySelector(
      ".wo-checkbox[data-category]",
    );
    if (!pairedCheckbox || pairedCheckbox.dataset.bundleIncluded === "true")
      return;

    // Sync variant selection
    const select = input
      .closest(".addon-product-block")
      ?.querySelector(".cfg-variant-select select");
    const pairedSelect = pairedCard.querySelector(".cfg-variant-select select");
    if (select && pairedSelect && pairedSelect.value !== select.value) {
      const option = pairedSelect.querySelector(
        `option[value="${select.value}"]`,
      );
      if (option) pairedSelect.value = select.value;
    }

    if (pairedCheckbox.checked === input.checked) return;

    pairedCheckbox.checked = input.checked;
    pairedCard
      .closest(".cfg-product")
      ?.classList.toggle("is-selected", input.checked);

    // Sync state
    if (!this.state.selectedAddons.has(pairedId)) {
      this.state.selectedAddons.set(pairedId, new Set());
    }
    const pairedSet = this.state.selectedAddons.get(pairedId);
    const variantId = pairedCheckbox.value;
    if (input.checked) {
      pairedSet.add(variantId);
    } else {
      pairedSet.delete(variantId);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Tabs & Navigation
  // Tab switching, category selection, scroll spy.
  // ═══════════════════════════════════════════════════════════════════════════

  initTabs() {
    this._findAll(".cfg-category[data-category-handle]").forEach((cat) => {
      const handle = cat.dataset.categoryHandle;
      const section = this._find(`[data-category-section="${handle}"]`);
      if (!section) cat.remove();
    });

    this._findAll(".cfg-tabs [data-tab]").forEach((tabBtn) => {
      const tabId = tabBtn.dataset.tab;
      const hasCategories = this._find(`.cfg-category[data-tab="${tabId}"]`);
      if (!hasCategories) tabBtn.hidden = true;
    });

    this._findAll(".cfg-category[data-tab]").forEach((cat) => {
      cat.hidden = cat.dataset.tab !== this.state.activeTab;
    });

    this._findAll("[data-tab-content]").forEach((content) => {
      content.hidden = content.dataset.tabContent !== this.state.activeTab;
    });

    const firstCategory = this._find(
      `.cfg-category[data-tab="${this.state.activeTab}"]:not([hidden])`,
    );
    if (firstCategory) {
      this.state.activeCategory = firstCategory.dataset.categoryHandle;
      firstCategory.classList.add("is-active");
    }

    this.initScrollSpy();
  }

  switchTab(tabId) {
    this.state.activeTab = tabId;

    this._findAll(".cfg-tabs [data-tab]").forEach((tab) => {
      const isActive = tab.dataset.tab === tabId;
      tab.classList.toggle("is-active", isActive);
      tab.setAttribute("aria-selected", isActive);
    });

    this._findAll(".cfg-category[data-tab]").forEach((cat) => {
      cat.hidden = cat.dataset.tab !== tabId;
    });

    this._findAll("[data-tab-content]").forEach((content) => {
      content.hidden = content.dataset.tabContent !== tabId;
    });

    const firstCategory = this._find(
      `.cfg-category[data-tab="${tabId}"]:not([hidden])`,
    );
    if (firstCategory) {
      this.selectCategory(firstCategory.dataset.categoryHandle);
    }
  }

  selectCategory(categoryId) {
    this.state.activeCategory = categoryId;

    this._findAll("[data-category-handle]").forEach((cat) => {
      cat.classList.toggle(
        "is-active",
        cat.dataset.categoryHandle === categoryId,
      );
    });

    this.scrollToCategory(categoryId);
  }

  scrollToCategory(categoryId) {
    const section = this._find(`#cfg-section-${categoryId}`);
    if (!section) return;
    this._isScrollingToCategory = true;
    clearTimeout(this._scrollLockTimer);
    section.scrollIntoView({ behavior: "smooth", block: "start" });
    this._scrollLockTimer = setTimeout(() => {
      this._isScrollingToCategory = false;
    }, 600);
  }

  initScrollSpy() {
    const productsCol = this._find(".cfg-col--products");
    if (!productsCol) return;
    this._scrollSpyCleanup?.();

    let rafId = null;
    const updateActiveCategory = () => {
      const containerTop = productsCol.getBoundingClientRect().top;
      const sections = productsCol.querySelectorAll("[data-category-section]");
      if (!sections.length) return;

      let active = null;
      let firstVisible = null;
      let lastVisible = null;

      for (const section of sections) {
        if (section.closest("[hidden]")) continue;
        if (!firstVisible) firstVisible = section;
        lastVisible = section;
        if (section.getBoundingClientRect().top <= containerTop + 1) {
          active = section;
        }
      }

      const { scrollTop, scrollHeight, clientHeight } = productsCol;
      if (scrollTop + clientHeight >= scrollHeight - 2) {
        active = lastVisible;
      }
      if (!active) active = firstVisible;

      const handle = active?.dataset.categorySection;
      if (handle && handle !== this.state.activeCategory) {
        this.state.activeCategory = handle;
        this._findAll("[data-category-handle]").forEach((cat) => {
          cat.classList.toggle(
            "is-active",
            cat.dataset.categoryHandle === handle,
          );
        });
      }
    };

    const onScroll = () => {
      if (this._isScrollingToCategory) return;
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateActiveCategory();
      });
    };

    productsCol.addEventListener("scroll", onScroll, { passive: true });
    updateActiveCategory();

    this._scrollSpyCleanup = () => {
      productsCol.removeEventListener("scroll", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Auto-select First Available Variant
  // Fallback variant selection when current combo is unavailable/sold-out.
  // ═══════════════════════════════════════════════════════════════════════════

  autoSelectFirstAvailable(variantPicker) {
    if (!variantPicker) variantPicker = this._find("variant-picker");
    if (!variantPicker) return null;

    const variantScript = variantPicker.querySelector("script[data-variant]");
    let variant = null;
    try {
      variant = variantScript ? JSON.parse(variantScript.textContent) : null;
    } catch (_) {}

    // Trigger when variant is null (no matching combo) OR sold out
    if (!variant || !variant.available) {
      const firstAvailOpts = (this.dataset.firstAvailableOptions || "")
        .split("|")
        .map((s) => s.trim())
        .filter(Boolean);

      if (firstAvailOpts.length > 0) {
        const fieldsets = variantPicker.querySelectorAll("fieldset");

        // 1. Set all inner radios silently (no events) to avoid
        //    multiple async fetches from the outer VariantPicker
        fieldsets.forEach((fieldset, idx) => {
          const targetText = firstAvailOpts[idx];
          if (!targetText) return;

          const radios = fieldset.querySelectorAll('input[type="radio"]');
          for (const radio of radios) {
            const label = radio.id
              ? variantPicker.querySelector(
                  `label[for="${CSS.escape(radio.id)}"]`,
                )
              : null;
            const labelText = label
              ? label.textContent.trim()
              : radio.closest("label")?.textContent.trim() || "";

            if (labelText === targetText) {
              radio.checked = true;
              break;
            }
          }
        });

        // 2. Resolve variant synchronously from embedded JSON
        //    (the variant script hasn't been updated yet since outer
        //    VariantPicker fetch is async)
        if (!this._productVariants) {
          const script = this._find("[data-product-variants]");
          if (script) {
            try {
              this._productVariants = JSON.parse(script.textContent);
            } catch (_) {}
          }
        }
        if (this._productVariants) {
          variant =
            this._productVariants.find(
              (v) =>
                v.available &&
                v.options.every((opt, i) => opt === firstAvailOpts[i]),
            ) || null;
        }

        // 3. Update configurator state with the resolved variant
        if (variant) {
          this.onVariantSwitch(variant);
        }

        // 4. Sync to outer picker: set outer radios + dispatch a single
        //    change on the last one so the outer VariantPicker re-renders
        //    with the correct combination in one fetch.
        this.syncFirstAvailableToOuter(variantPicker);
      }
    }

    return variant;
  }

  /**
   * Copy the inner picker's current checked state to the outer picker radios,
   * then dispatch a single change event so the outer VariantPicker fetches
   * the correct combination once (instead of once per option).
   */
  syncFirstAvailableToOuter(picker) {
    const form = this._outerForm;
    if (!form) return;
    const formId = form.getAttribute("id");
    if (!formId) return;

    let lastOuterRadio = null;

    picker.querySelectorAll('input[type="radio"]:checked').forEach((inner) => {
      const outerName = inner.name.replace("-featured_product", "");
      let outerRadio = document.querySelector(
        `input[type="radio"][form="${CSS.escape(formId)}"][name="${CSS.escape(outerName)}"][value="${CSS.escape(inner.value)}"]`,
      );
      if (outerRadio && picker.contains(outerRadio)) outerRadio = null;
      if (!outerRadio) {
        outerRadio = form.querySelector(
          `input[name="${CSS.escape(outerName)}"][value="${CSS.escape(inner.value)}"]`,
        );
      }
      if (outerRadio && !outerRadio.checked) {
        outerRadio.checked = true;
        lastOuterRadio = outerRadio;
      }
    });

    // Dispatch change only once (on the last updated radio) so the outer
    // VariantPicker sees the full combination and does a single fetch.
    if (lastOuterRadio) {
      lastOuterRadio.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  /**
   * Force-select the first available variant every time the modal opens,
   * regardless of whether the current variant is available.
   * This ensures the modal always starts with the first in-stock option.
   */
  forceSelectFirstAvailable() {
    const variantPicker = this._find("variant-picker");
    if (!variantPicker) return null;

    const firstAvailOpts = (this.dataset.firstAvailableOptions || "")
      .split("|")
      .map((s) => s.trim())
      .filter(Boolean);

    if (firstAvailOpts.length > 0) {
      const fieldsets = variantPicker.querySelectorAll("fieldset");
      fieldsets.forEach((fieldset, idx) => {
        const targetText = firstAvailOpts[idx];
        if (!targetText) return;

        const radios = fieldset.querySelectorAll('input[type="radio"]');
        for (const radio of radios) {
          const label = radio.id
            ? variantPicker.querySelector(
                `label[for="${CSS.escape(radio.id)}"]`,
              )
            : null;
          const labelText = label
            ? label.textContent.trim()
            : radio.closest("label")?.textContent.trim() || "";

          if (labelText === targetText && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event("change", { bubbles: true }));
            break;
          }
        }
      });
    }

    // Re-read variant after switching
    const variantScript = variantPicker.querySelector("script[data-variant]");
    let variant = null;
    try {
      variant = variantScript ? JSON.parse(variantScript.textContent) : null;
    } catch (_) {}

    return variant;
  }

  /**
   * In the flatteners section, group consecutive Flattener + (Flattener Adapter|Adapter)
   * by product type (two cards joined together). Runs when modal opens.
   * Wrapped in try/catch so a DOM/grouping error never breaks the modal.
   */
  _groupFlattenerWithAdapter() {
    try {
      const root = this._modal || this;
      const section = root.querySelector("#cfg-section-flatteners");
      if (!section) return;
      const cards = Array.from(
        section.querySelectorAll(":scope > .addon-product-block"),
      );
      let i = 0;
      while (i < cards.length - 1) {
        const card = cards[i];
        const next = cards[i + 1];
        if (!card?.isConnected || !next?.isConnected) {
          i += 1;
          continue;
        }
        const type = (card.dataset.productType || "").trim();
        const nextType = (next.dataset.productType || "").trim();
        const isAdapter =
          nextType === "Flattener Adapter" || nextType === "Adapter";
        if (type === "Flattener" && isAdapter) {
          const group = document.createElement("div");
          group.className = "cfg-flattener-group";
          const hr = document.createElement("hr");
          hr.className = "cfg-flattener-group__hr";
          hr.setAttribute("aria-hidden", "true");
          card.before(group);
          group.append(card, hr, next);
          cards.splice(i + 1, 1);
        }
        i += 1;
      }
    } catch (_) {
      // Do not break modal open if grouping fails
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Modal Open / Close
  // Append to body, sync pickers, animation, restore on close.
  // ═══════════════════════════════════════════════════════════════════════════

  openModal() {
    const modal = this._modal;
    if (!modal) return;

    // Append to <body> (like x-modal) so it escapes any parent stacking
    // context and sits above sticky elements (e.g. the site header).
    if (modal.parentElement !== document.body) {
      this._modalOriginalParent = modal.parentElement;
      document.body.append(modal);
    }

    modal.hidden = false;
    document.body.style.overflow = "hidden";

    // Fix bfcache desync: browser form-restoration may flip the radio
    // after pageshow, so re-align the visual outline to the actual :checked radio.
    const checkedOption = this._find(
      'input[name="configurator-option"]:checked',
    );
    if (checkedOption) {
      this._findAll(".cfg-option").forEach((opt) => {
        opt.classList.toggle(
          "cfg-option--selected",
          opt.contains(checkedOption),
        );
      });
    }

    // Sync inner picker to match outer variant-picker state directly
    this.syncOuterToInnerNow();

    // Read current variant and update button state
    const variantPicker = this._find("variant-picker");
    let variant = null;
    if (variantPicker) {
      const variantScript = variantPicker.querySelector("script[data-variant]");
      try {
        variant = variantScript ? JSON.parse(variantScript.textContent) : null;
      } catch (_) {}
    } else {
      // No variant-picker (e.g. bundle product with default variant only).
      // Use the checked radio's value to confirm availability.
      const checkedRadio = this._find(
        'input[name="configurator-option"]:checked',
      );
      if (checkedRadio && checkedRadio.value) {
        variant = { id: checkedRadio.value, available: true };
      }
    }
    this.updateBuyButtonState(variant);

    // Sync addon card images/selects to match the current variant selection
    this.syncAddonVariants();
    this.syncAddonCardImages();

    // Group Flattener + (Flattener Adapter|Adapter) by product type (non-blocking)
    this._groupFlattenerWithAdapter();
  }

  /**
   * Directly sync inner picker radios to match outer form's checked state.
   * Dispatches change on mismatched radios so the theme re-renders properly.
   */
  syncOuterToInnerNow() {
    const picker = this._innerPicker;
    const form = this._outerForm;
    if (!picker || !form) return;

    const formId = form.getAttribute("id");
    if (!formId) return;

    // 1. Replace inner fieldsets with clones from the outer picker so that
    //    is-disabled classes and other availability states are up-to-date.
    const outerPicker = form
      .closest("[id^='shopify-section-']")
      ?.querySelector("variant-picker:not([context='featured_product'])");
    if (outerPicker) {
      const outerFieldsets = outerPicker.querySelectorAll("fieldset");
      const innerFieldsets = picker.querySelectorAll("fieldset");
      outerFieldsets.forEach((outerFs, index) => {
        if (innerFieldsets[index]) {
          const clone = outerFs.cloneNode(true);
          // Remove form attributes to avoid radio group conflicts
          clone
            .querySelectorAll('input[type="radio"][form]')
            .forEach((r) => r.removeAttribute("form"));
          innerFieldsets[index].replaceWith(clone);
        }
      });

      // Fix duplicate IDs introduced by cloning outer fieldsets
      this.deduplicatePickerIds(picker);

      // Sync the variant JSON script
      const outerScript = outerPicker.querySelector("script[data-variant]");
      const innerScript = picker.querySelector("script[data-variant]");
      if (outerScript && innerScript) {
        innerScript.textContent = outerScript.textContent;
      }
    }

    // 2. Collect outer checked radio values and apply to inner picker.
    //    Outer radios use form="..." attribute and may live outside <form>.
    const outerValues = new Map();
    document
      .querySelectorAll(
        `input[type="radio"][form="${CSS.escape(formId)}"]:checked`,
      )
      .forEach((radio) => {
        if (picker.contains(radio)) return;
        outerValues.set(radio.name, radio.value);
      });
    // Fallback: radios physically inside the form element
    form.querySelectorAll('input[type="radio"]:checked').forEach((radio) => {
      if (picker.contains(radio)) return;
      if (!outerValues.has(radio.name)) {
        outerValues.set(radio.name, radio.value);
      }
    });

    if (outerValues.size === 0) return;

    // Only set radio.checked — do NOT dispatch change events.
    // CSS :checked + .block-swatch handles the visual state automatically.
    // Dispatching change triggers theme re-render chains that break
    // the outer variant-picker.
    picker.querySelectorAll("fieldset").forEach((fieldset) => {
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      if (!radios.length) return;

      const innerName = radios[0].name;
      const outerName = innerName.replace("-featured_product", "");
      const targetValue =
        outerValues.get(outerName) || outerValues.get(innerName);
      if (!targetValue) return;

      for (const radio of radios) {
        radio.checked = radio.value === targetValue;
      }
    });
  }

  closeModal() {
    const modal = this._modal;
    if (!modal || modal.hidden) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const duration = prefersReducedMotion ? 0 : 300;

    modal.classList.add("is-closing");
    setTimeout(() => {
      modal.hidden = true;
      modal.classList.remove("is-closing");
      // Restore modal to its original position inside this component
      if (this._modalOriginalParent && modal.parentElement === document.body) {
        this._modalOriginalParent.appendChild(modal);
        this._modalOriginalParent = null;
      }
      document.body.style.overflow = "";
    }, duration);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Badges & Total Price
  // Category badge counts, total price calculation and display.
  // ═══════════════════════════════════════════════════════════════════════════

  updateCategoryBadges() {
    this._findAll("[data-category-handle]").forEach((btn) => {
      const categoryId = btn.dataset.categoryHandle;
      const selectedIds =
        this.state.selectedAddons.get(categoryId) || new Set();

      let userCount = 0;
      let bundleCount = 0;
      const freeIds = this.state.freeIncludedIds || new Set();
      selectedIds.forEach((id) => {
        if (this.state.bundleIncludedIds.has(id) || freeIds.has(id)) {
          bundleCount++;
        } else {
          userCount++;
        }
      });

      // Helper: create or update a badge with bg div + number text node
      const setBadge = (existing, className, count, parent) => {
        if (count > 0) {
          if (!existing) {
            existing = document.createElement("div");
            existing.className = className;
            const bg = document.createElement("div");
            bg.className = "cfg-badge__bg";
            existing.appendChild(bg);
            const countEl = document.createElement("div");
            countEl.className = "cfg-badge__count";
            existing.appendChild(countEl);
            parent.appendChild(existing);
          }
          existing.querySelector(".cfg-badge__count").textContent = count;
          return existing;
        } else {
          existing?.remove();
          return null;
        }
      };

      setBadge(
        btn.querySelector(".cfg-badge--user-selected"),
        "cfg-badge cfg-badge--user-selected",
        userCount,
        btn,
      );
      setBadge(
        btn.querySelector(".cfg-badge--bundle-included"),
        "cfg-badge cfg-badge--bundle-included",
        bundleCount,
        btn,
      );
    });
  }

  _sessionKey() {
    return `cfg-selections:${window.location.pathname}`;
  }

  saveSelectionState() {
    const data = {};
    this.state.selectedAddons.forEach((ids, category) => {
      if (ids.size > 0) data[category] = Array.from(ids);
    });
    const checkedOption = this._find(
      'input[name="configurator-option"]:checked',
    );
    if (checkedOption && checkedOption.dataset.optionType !== "main") {
      data.__selectedOptionProductId = checkedOption.dataset.optionProductId;
    }
    try {
      sessionStorage.setItem(this._sessionKey(), JSON.stringify(data));
    } catch (_) {}
  }

  restoreOptionFromStorage() {
    let data;
    try {
      const raw = sessionStorage.getItem(this._sessionKey());
      if (!raw) return;
      data = JSON.parse(raw);
    } catch (_) {
      return;
    }
    if (!data.__selectedOptionProductId) return;
    const radio = this._find(
      `input[name="configurator-option"][data-option-product-id="${data.__selectedOptionProductId}"]:not([data-option-type="main"])`,
    );
    if (!radio) return;
    const current = this._find('input[name="configurator-option"]:checked');
    if (current) current.checked = false;
    radio.checked = true;
  }

  restoreSelectionState() {
    let data;
    try {
      const raw = sessionStorage.getItem(this._sessionKey());
      if (!raw) return;
      data = JSON.parse(raw);
    } catch (_) {
      return;
    }
    Object.entries(data).forEach(([category, ids]) => {
      if (!Array.isArray(ids)) return;
      ids.forEach((id) => {
        const cb = this._find(
          `.wo-checkbox[value="${id}"][data-category="${category}"]`,
        );
        if (!cb || cb.dataset.bundleIncluded === "true") return;
        cb.checked = true;
        if (!this.state.selectedAddons.has(category)) {
          this.state.selectedAddons.set(category, new Set());
        }
        this.state.selectedAddons.get(category).add(id);
        const card = cb.closest(".cfg-product");
        if (card) card.classList.add("is-selected");

        // Sync paired category
        this.syncPairedCategory(cb, category);
      });
    });
    this.updateCategoryBadges();
    this.updatePrices();
  }

  rebuildSelectionsFromDom() {
    this.state.selectedAddons.clear();
    this._findAll(".wo-checkbox[data-category]:checked").forEach((input) => {
      if (input.dataset.bundleIncluded === "true") return;
      const category = input.dataset.category;
      if (!this.state.selectedAddons.has(category)) {
        this.state.selectedAddons.set(category, new Set());
      }
      this.state.selectedAddons.get(category).add(String(input.value));
      const card = input.closest(".cfg-product");
      if (card) card.classList.add("is-selected");
    });
    this.updateCategoryBadges();
    this.updatePrices();
  }

  resetAddonSelections() {
    this.state.selectedAddons.clear();
    try {
      sessionStorage.removeItem(this._sessionKey());
    } catch (_) {}
    this._findAll(".wo-checkbox[data-category]").forEach((cb) => {
      if (cb.dataset.bundleIncluded === "true") return;
      if (cb.dataset.freeIncluded === "true") return;
      cb.checked = false;
      cb.disabled = false;
      const card = cb.closest(".cfg-product");
      if (card) card.classList.remove("is-selected");
    });
    // Reset option back to main (regular product with bundle options)
    const mainRadio = this._find(
      'input[name="configurator-option"][data-option-type="main"]',
    );
    if (mainRadio && !mainRadio.checked) {
      mainRadio.checked = true;
      this.onOptionChange(mainRadio);
    }

    // Re-apply permanent bundle inclusions (bundle product page, or after option reset)
    if (this.state.bundleIncludedIds.size > 0) {
      this.applyBundleInclusions();
    }

    // Re-register free items (Liquid-rendered data-free-included checkboxes)
    this.registerFreeItems();

    this.updatePrices();
    this.updateCategoryBadges();
  }

  updatePrices() {
    let addonsTotal = 0;
    let discountedAddonsTotal = 0;
    const visualPaired = new Set(["tripod-visual", "mounting-plate-visual"]);

    this._findAll(".wo-checkbox[data-category]:checked").forEach((input) => {
      if (input.dataset.bundleIncluded === "true") return;
      if (visualPaired.has(input.dataset.category)) return;
      const price = parseInt(input.dataset.price, 10) || 0;
      addonsTotal += price;
      discountedAddonsTotal += this.getPromoPrice(price, input);
    });

    this.state.addonsPrice = discountedAddonsTotal;

    const optionRadio = this._find(
      'input[name="configurator-option"]:checked',
    );
    const optionPrice = parseInt(optionRadio?.dataset.price, 10);
    const originalBasePrice = Number.isFinite(optionPrice)
      ? optionPrice
      : this.state.basePrice;
    const compareAtBasePrice =
      parseInt(optionRadio?.dataset.compareAtPrice, 10) || 0;
    const discountedBasePrice = this.getPromoPrice(
      originalBasePrice,
      optionRadio,
    );
    const originalTotalPrice =
      Math.max(compareAtBasePrice, originalBasePrice) + addonsTotal;
    const totalPrice = discountedBasePrice + this.state.addonsPrice;
    this.state.basePrice = discountedBasePrice;

    this._findAll("[data-configurator-original-total]").forEach((el) => {
      el.textContent = this.formatMoney(originalTotalPrice, false);
      el.hidden = totalPrice >= originalTotalPrice;
    });
    this._findAll("[data-configurator-total-price]").forEach((el) => {
      el.textContent = this.formatMoney(totalPrice);
      el.classList.toggle("text-on-sale", totalPrice < originalTotalPrice);
      el.classList.toggle("text-subdued", totalPrice >= originalTotalPrice);
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION: Cart Submission
  // Build cart payload, add items, close modal on success.
  // ═══════════════════════════════════════════════════════════════════════════

  submit() {
    const form = this._find("[data-configurator-form]");
    const mainIdInput = this._find("[data-configurator-main-id]");
    const addonInputsContainer = this._find("[data-configurator-addon-inputs]");

    if (!form || !mainIdInput || !addonInputsContainer) return;

    addonInputsContainer.innerHTML = "";

    let index = 1;
    const visualPairedSubmit = new Set([
      "tripod-visual",
      "mounting-plate-visual",
    ]);
    this._findAll(".wo-checkbox[data-category]:checked").forEach((input) => {
      if (input.dataset.bundleIncluded === "true") return;
      if (visualPairedSubmit.has(input.dataset.category)) return;

      const card = input.closest(".addon-product-block");
      const select = card?.querySelector(".cfg-variant-select select");
      const variantId = (select && select.value) || input.value;

      const hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = `items[${index}][id]`;
      hiddenInput.value = variantId;
      addonInputsContainer.appendChild(hiddenInput);
      index++;
    });

    // Also include free items (hidden inputs inside the free-items slot)
    const freeSlot = this._find("[data-configurator-free-items]");
    if (freeSlot) {
      freeSlot
        .querySelectorAll('input[type="hidden"][name^="items["]')
        .forEach((input) => {
          if (!input.value) return;
          const hiddenInput = document.createElement("input");
          hiddenInput.type = "hidden";
          hiddenInput.name = `items[${index}][id]`;
          hiddenInput.value = input.value;
          addonInputsContainer.appendChild(hiddenInput);
          index++;
        });
    }

    this.submitToCart(form);
  }

  async submitToCart(form) {
    const submitBtn = this._find("[data-configurator-submit-wrapper] button");

    if (submitBtn) {
      submitBtn.setAttribute("aria-busy", "true");
      submitBtn.disabled = true;
    }

    try {
      // Collect sections that need re-rendering (e.g. cart-drawer)
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        }),
      );

      const formData = new FormData(form);
      formData.set("sections", sectionsToBundle.join(","));

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        body: formData,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      if (!response.ok) {
        const error = await response.json();
        const msg = error.description || "Error adding to cart";
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: { message: msg },
          }),
        );
        // Fallback: show banner if no listener handled it
        const banner = document.querySelector(".cart-error-banner");
        if (banner) {
          banner.textContent = msg;
          banner.hidden = false;
        }
        return;
      }

      const responseJson = await response.json();

      // Fallback: redirect if cart type is "page" or already on cart page
      if (
        window.themeVariables?.settings?.cartType === "page" ||
        window.themeVariables?.settings?.pageType === "cart"
      ) {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      // Fetch updated cart and dispatch event → CartDrawer picks it up
      const cartContent = await (
        await fetch(`${Shopify.routes.root}cart.js`)
      ).json();
      cartContent["sections"] = responseJson["sections"];

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartContent,
          },
        }),
      );

      this.resetAddonSelections();
      this.closeModal();
    } catch (e) {
      // The finally block re-enables the button so the shopper can retry.
    } finally {
      if (submitBtn) {
        submitBtn.removeAttribute("aria-busy");
        submitBtn.disabled = false;
      }
    }
  }
}

if (!customElements.get("product-configurator")) {
  customElements.define("product-configurator", ProductConfigurator);
}

/**
 * Global linked-option auto-correction for VariantPicker (non-configurator UI).
 *
 * When a product has multiple option groups, changing one option can make the
 * current combination unavailable / sold out. This helper automatically
 * switches disabled option groups to an available alternative.
 */
(() => {
  const MAX_FIX_ATTEMPTS = 6;
  const attemptsByForm = new WeakMap();

  let lastUserInteractedOptionPosition = null;
  let lastUserInteractedIsColor = false;
  let isUserInteraction = false;

  document.addEventListener(
    "change",
    (e) => {
      const target = e.target;
      if (target && target.matches('variant-picker input[type="radio"]')) {
        if (e.isTrusted) {
          isUserInteraction = true;
          lastUserInteractedOptionPosition = target.getAttribute("data-option-position");
          
          const fieldset = target.closest("fieldset.variant-picker__option");
          const legendText = fieldset?.querySelector("legend")?.textContent || "";
          lastUserInteractedIsColor = /color|顏色/i.test(legendText);
        }
      }
    },
    true
  );

  const isDisabledChoice = (picker, input) => {
    if (!input || input.disabled) return true;
    if (input.classList.contains("is-disabled")) return true;

    if (input.id) {
      const label = picker.querySelector(
        `label[for="${CSS.escape(input.id)}"]`,
      );
      if (label?.classList.contains("is-disabled")) return true;
    }

    return input.closest("label")?.classList.contains("is-disabled") ?? false;
  };

  const findFirstEnabled = (picker, fieldset) =>
    Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
      (r) => !isDisabledChoice(picker, r),
    ) ?? null;

  const autoFixUnavailableSelection = (form, variant) => {
    if (!form) return;
    const formId = form.getAttribute("id");
    if (!formId) return;

    const picker = document.querySelector(
      `variant-picker[form-id="${CSS.escape(formId)}"]`,
    );
    if (!picker) return;

    if (variant?.id && variant.available !== false) {
      attemptsByForm.delete(form);
      isUserInteraction = false;
      return;
    }

    if (isUserInteraction && !lastUserInteractedIsColor) {
      isUserInteraction = false;
      return;
    }

    const attempts = attemptsByForm.get(form) || 0;
    if (attempts >= MAX_FIX_ATTEMPTS) return;
    attemptsByForm.set(form, attempts + 1);

    const fieldsets = picker.querySelectorAll(
      "fieldset.variant-picker__option",
    );
    let lastChanged = null;
    // Replace any checked-but-disabled choices with the first enabled alternative
    fieldsets.forEach((fs) => {
      const checked = fs.querySelector('input[type="radio"]:checked');
      if (!checked || !isDisabledChoice(picker, checked)) return;

      if (
        isUserInteraction &&
        checked.getAttribute("data-option-position") ===
          lastUserInteractedOptionPosition
      ) {
        return;
      }

      const replacement = findFirstEnabled(picker, fs);
      if (replacement && replacement !== checked) {
        replacement.checked = true;
        lastChanged = replacement;
      }
    });
    // Fallback: if no choices were explicitly disabled but the combo is still
    // unavailable, nudge the most specific option group (last) to converge.
    if (!lastChanged) {
      const fieldsetArray = Array.from(fieldsets);
      for (let i = fieldsetArray.length - 1; i >= 0; i--) {
        const fs = fieldsetArray[i];
        const checked = fs.querySelector('input[type="radio"]:checked');

        if (
          isUserInteraction &&
          checked?.getAttribute("data-option-position") ===
            lastUserInteractedOptionPosition
        ) {
          continue;
        }

        const alternative = Array.from(
          fs.querySelectorAll('input[type="radio"]'),
        ).find((r) => r !== checked && !isDisabledChoice(picker, r));

        if (alternative) {
          alternative.checked = true;
          lastChanged = alternative;
          break;
        }
      }
    }

    if (lastChanged) {
      lastChanged.dispatchEvent(new Event("change", { bubbles: true }));
    } else {
      isUserInteraction = false;
    }
  };

  document.addEventListener(
    "variant:change",
    (e) => {
      const form =
        e.target instanceof HTMLFormElement
          ? e.target
          : e.target?.closest?.("form");
      requestAnimationFrame(() =>
        autoFixUnavailableSelection(form, e.detail?.variant),
      );
    },
    true,
  );
})();

/**
 * =============================================================================
 * GLOBAL SHOPIFY SECTION ANCHOR LINK HEALER
 * =============================================================================
 *
 * Problem: Shopify OS 2.0 section wrappers have dynamic IDs like:
 * #shopify-section-template--19961211715801__rich_text_7gNXd4
 * When a template is updated or duplicated, the template timestamp changes,
 * breaking all anchor links pointing to it.
 *
 * Solution: Progressive enhancement that extracts the stable section suffix
 * (e.g., __rich_text_7gNXd4) and finds the updated matching element.
 */
function fixShopifySectionAnchorLinks() {
  // 1. Intercept intra-page anchor clicks
  document.addEventListener("click", (e) => {
    // Intercept any intra-page hashtag link
    const link = e.target.closest('a[href*="#"]');
    if (!link) return;

    // Ensure it targets the same page path
    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname) return;

    const hash = url.hash;
    if (!hash || hash === "#") return;

    // If exact ID exists (link is not broken), browser behavior works natively
    if (document.getElementById(hash.substring(1))) return;

    // Extract stable section suffix
    // If it's a long #shopify-section-... link, grab the part after '__'
    // If it's already a short link like #rich_text_7gNXd4, use it directly
    let stableSectionId = hash.substring(1);
    if (hash.includes("__")) {
      const parts = hash.split("__");
      stableSectionId = parts[parts.length - 1];
    }

    // Find updated element with that suffix
    const targetElement = document.querySelector(
      `[id$="__${stableSectionId}"]`,
    );

    if (targetElement) {
      e.preventDefault();
      targetElement.scrollIntoView({ behavior: "smooth" });
      // Update URL safely
      history.pushState(null, null, `#${targetElement.id}`);
    }
  });

  // 2. Heal broken standard navigation links upon page load
  if (window.location.hash) {
    const hash = window.location.hash;

    // If exact ID missing on load
    if (!document.getElementById(hash.substring(1)) && hash !== "#") {
      let stableSectionId = hash.substring(1);
      if (hash.includes("__")) {
        const parts = hash.split("__");
        stableSectionId = parts[parts.length - 1];
      }

      const targetElement = document.querySelector(
        `[id$="__${stableSectionId}"]`,
      );

      if (targetElement) {
        // Defer jump slightly to ensure layout engines are fully painted
        setTimeout(() => {
          targetElement.scrollIntoView({ behavior: "smooth" });
          history.replaceState(null, null, `#${targetElement.id}`);
        }, 150);
      }
    }
  }
}

// Initialize when ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", fixShopifySectionAnchorLinks);
} else {
  fixShopifySectionAnchorLinks();
}

class HorizontalScrollGallery extends HTMLElement {
  async connectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.videoObserver?.disconnect();
    clearTimeout(this.videoSettleTimer);
    const controller = new AbortController();
    this.abortController = controller;
    await customElements.whenDefined("scroll-carousel");
    if (this.querySelector("video-media")) {
      await customElements.whenDefined("video-media");
    }
    if (controller.signal.aborted || !this.isConnected) return;

    const carousel = this.querySelector("scroll-carousel");
    if (!carousel) return;
    const controls = this.querySelector(".horizontal-scroll-gallery__controls");
    const previous = this.querySelector("[data-gallery-previous]");
    const next = this.querySelector("[data-gallery-next]");
    const options = { signal: controller.signal };
    let pendingIndex = null;
    const closestIndex = () => {
      const bounds = carousel.getBoundingClientRect();
      const style = getComputedStyle(carousel);
      const rtl = style.direction === "rtl";
      const padding = parseFloat(style.paddingInlineStart) || 0;
      const anchor = rtl ? bounds.right - padding : bounds.left + padding;
      const distances = carousel.cells.map((cell) => {
        const rect = cell.getBoundingClientRect();
        return Math.abs((rtl ? rect.right : rect.left) - anchor);
      });
      return distances.indexOf(Math.min(...distances));
    };
    const move = (direction) => {
      const index = pendingIndex ?? closestIndex();
      const instant = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (direction === "next" && index === carousel.cells.length - 1) {
        const maximum = carousel.scrollWidth - carousel.clientWidth;
        carousel.scrollTo({
          left: getComputedStyle(carousel).direction === "rtl" ? -maximum : maximum,
          behavior: instant ? "auto" : "smooth",
        });
        return;
      }
      pendingIndex = Math.max(0, Math.min(
        index + (direction === "next" ? 1 : -1), carousel.cells.length - 1,
      ));
      carousel.select(pendingIndex, {
        instant,
      });
    };
    const updateControls = () => {
      if (!controls) return;
      const maximum = carousel.scrollWidth - carousel.clientWidth;
      const position = Math.abs(carousel.scrollLeft);
      controls.hidden = maximum <= 1;
      previous.disabled = position <= 1;
      next.disabled = position >= maximum - 1;
    };

    previous?.addEventListener("click", () => move("previous"), options);
    next?.addEventListener("click", () => move("next"), options);
    carousel.addEventListener("scroll", updateControls, { ...options, passive: true });
    const clearPendingIndex = () => { pendingIndex = null; };
    carousel.addEventListener("carousel:settle", clearPendingIndex, options);
    carousel.addEventListener("wheel", clearPendingIndex, { ...options, passive: true });
    carousel.addEventListener("pointerdown", clearPendingIndex, options);
    carousel.addEventListener("keydown", (event) => {
      if (event.target !== carousel || event.altKey || event.ctrlKey || event.metaKey) return;
      const rtl = getComputedStyle(carousel).direction === "rtl";
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        move((event.key === "ArrowRight") !== rtl ? "next" : "previous");
      }
    }, options);
    const updateLayout = () => {
      const padding = getComputedStyle(carousel).paddingInlineStart;
      const previousPadding = carousel.style.scrollPaddingInline;
      carousel.style.scrollPaddingInline = padding;
      if (previousPadding && previousPadding !== padding) {
        carousel.select(closestIndex(), { instant: true });
      }
      clearPendingIndex();
      updateControls();
    };
    this.resizeObserver = new ResizeObserver(updateLayout);
    this.resizeObserver.observe(carousel);
    this.resizeObserver.observe(this);
    updateLayout();
    this.setupVideoPlayback(carousel, closestIndex, controller.signal);
  }

  setupVideoPlayback(carousel, closestIndex, signal) {
    const mediaElements = Array.from(carousel.querySelectorAll("video-media"));
    if (!mediaElements.length) return;

    const visibleMedia = new Set();
    let activeMedia = null;
    let activeIndex = closestIndex();
    let requestedIndex = null;
    let scrolling = false;
    const options = { signal };
    const updatePlayback = () => {
      if (signal.aborted || !this.isConnected) return;
      const cell = carousel.cells[activeIndex];
      const media = cell?.querySelector("video-media");
      const isActive = !scrolling && !document.hidden && visibleMedia.has(media);
      mediaElements.forEach((item) => {
        const video = item.querySelector("video");
        if (video) {
          video.loop = isActive && item === media && cell.hasAttribute("data-gallery-loop");
        }
      });
      const previousMedia = activeMedia;
      activeMedia = isActive ? media : null;
      if (activeMedia && activeMedia !== previousMedia && cell.hasAttribute("data-gallery-autoplay")) {
        media.play();
      }
    };
    const settle = () => {
      clearTimeout(this.videoSettleTimer);
      scrolling = false;
      const maximum = carousel.scrollWidth - carousel.clientWidth;
      const atEnd = maximum > 1 && Math.abs(carousel.scrollLeft) >= maximum - 1;
      activeIndex = requestedIndex ?? (atEnd ? carousel.cells.length - 1 : closestIndex());
      requestedIndex = null;
      updatePlayback();
    };
    const scheduleSettle = () => {
      clearTimeout(this.videoSettleTimer);
      this.videoSettleTimer = setTimeout(settle, 150);
    };

    carousel.addEventListener("carousel:select", (event) => {
      requestedIndex = event.detail.index;
      scheduleSettle();
    }, options);
    carousel.addEventListener("scroll", () => {
      scrolling = true;
      updatePlayback();
      scheduleSettle();
    }, { ...options, passive: true });
    carousel.addEventListener("carousel:settle", settle, options);
    carousel.addEventListener("wheel", () => { requestedIndex = null; }, { ...options, passive: true });
    carousel.addEventListener("pointerdown", () => { requestedIndex = null; }, options);
    document.addEventListener("visibilitychange", updatePlayback, options);

    this.videoObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          visibleMedia.add(entry.target);
        } else {
          visibleMedia.delete(entry.target);
        }
      }
      updatePlayback();
    });
    mediaElements.forEach((media) => this.videoObserver.observe(media));
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.videoObserver?.disconnect();
    clearTimeout(this.videoSettleTimer);
    this.querySelectorAll("video").forEach((video) => video.pause());
  }
}

if (!customElements.get("horizontal-scroll-gallery")) {
  customElements.define("horizontal-scroll-gallery", HorizontalScrollGallery);
}

/**
 * =============================================================================
 * PROMO PRICE: TIME-WINDOW FALLBACK
 * =============================================================================
 *
 * snippets/promo-price.liquid renders the promo price and badge only inside the
 * window set in Theme settings > Promo price badge. Cached HTML (full-page
 * cache, section re-renders on variant change, quick view, recommendations, an
 * open tab) can outlive that window, so this swaps promo markup back to the
 * original markup whenever the current time is outside the window.
 * The window comes from the data-promo-price-starts / data-promo-price-ends
 * attributes (Unix seconds, 0 = no limit) rendered by the snippet; nothing is
 * hardcoded here.
 * =============================================================================
 */
(function initPromoPriceWindow() {
  const SELECTOR = "[data-promo-price-ends]";
  const MAX_TIMEOUT = 2147483647; // setTimeout limit (~24.8 days)

  const windowOf = (el) => ({
    starts: (parseInt(el.getAttribute("data-promo-price-starts"), 10) || 0) * 1000,
    ends: (parseInt(el.getAttribute("data-promo-price-ends"), 10) || 0) * 1000,
  });

  const isOutsideWindow = (el, now = Date.now()) => {
    const { starts, ends } = windowOf(el);
    return (starts > 0 && now < starts) || (ends > 0 && now >= ends);
  };

  const revert = (root = document) => {
    const found = [];
    if (root.nodeType === 1 && root.matches(SELECTOR)) found.push(root);
    root.querySelectorAll?.(SELECTOR).forEach((el) => found.push(el));

    found.forEach((el) => {
      if (!el.isConnected || !isOutsideWindow(el)) return;
      const fallback = el.querySelector(":scope > template[data-promo-price-fallback]");
      if (fallback) {
        el.replaceWith(fallback.content.cloneNode(true));
      } else {
        el.remove();
      }
    });
  };

  let observer = null;
  const watch = () => {
    revert();
    if (observer || typeof MutationObserver === "undefined" || !document.body) return;
    observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach((n) => n.nodeType === 1 && revert(n)));
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const start = () => {
    const first = document.querySelector(SELECTOR);
    if (!first) return;
    if (isOutsideWindow(first)) {
      watch();
      return;
    }
    // Inside the window: flip at the end time if the tab stays open.
    const { ends } = windowOf(first);
    const delay = ends - Date.now();
    if (ends > 0 && delay > 0 && delay < MAX_TIMEOUT) {
      setTimeout(watch, delay + 1000);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
